import React from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  FormCheck,
  InputGroup,
  Col,
  Tooltip,
  Row,
  OverlayTrigger,
  Modal,
} from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap';
import { Prompt } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { validate, ValidationError } from 'class-validator';
import moment from 'moment';
import {
  Bloodwork,
  BloodworkRange,
  PanelField,
  PanelRange,
  User,
  Client,
  UserProfile,
  WhiteBloodCellDifferentialUnit,
  WhiteBloodCellDifferentialUnitType,
  MenstrualPhase,
  I3_SECTIONS,
  CustomMarker,
  definitionsForDefaults,
  gatherCustomMarkersAcrossTests,
  bySectionOrder,
  groupBySectionValue,
  practitionerSectionOptionValue,
  sectionGroupKey,
  sectionOrderAfterRename,
  learnDefinitionsFromMarkers,
  CustomMarkerDefinition,
  CUSTOM_MARKER_UPGRADE_MODAL,
} from '../../models';
import { v4 as uuidv4 } from 'uuid';
import { BloodworkEntryStore } from '../../stores/bloodwork-entry-store';
import { MathUtils } from '../../utils/MathUtils';
/*
 * Direct, not through the models barrel: this module imports nothing but
 * MathUtils, so reaching it straight cannot draw the form into the import cycle
 * the barrel exists to order. The same reasoning as unitConversions.
 */
import {
  deriveBilirubinDirect,
  deriveBilirubinIndirect,
  deriveEgfr,
  deriveGlobulin,
  deriveVldl,
  deriveMchConcentration,
  deriveMeanCorpuscularHGB,
  deriveMeanCorpuscularVolume,
  deriveRatio,
  deriveSaturation,
  deriveTestosteroneBioavailablePercent,
  isMeasured,
  numericValue,
  ratiosInvolving,
} from '../../models/BloodworkDerivation';
import { profileSaveErrorMessage } from '../../utils/profileSaveError';
import { scrollPanelIntoViewUntilItLands } from '../../utils/scrollPanelIntoView';
import Select from 'react-select';
import { GenericModal } from '../common/generic-modal';
import { TimeSavingFeaturesModal } from './time-saving-features-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LABCORP_MVX_PLUS_URL } from '../../models/MetabolicVulnerability';
import { Upload } from './upload';
import { CLIENT_SELECT_MAX_WIDTH } from '../client/client-select-width';
import { mvxVisible } from '../../utils/mvxVisibility';
import { nmrLipoProfileVisible } from '../../utils/nmrLipoProfileVisibility';
import { vldlVisible } from '../../utils/vldlVisibility';
import { NMR_PARTICLE_ATTRIBUTES } from '../../models/PanelField';
import {
  assayLimitSubstitution,
  fallbackAssayLimitSubstitution,
  parseAssayLimit,
} from '../../utils/assayLimits';
import {
  CardCollapseToggle,
  CustomMarkersHeader,
  CustomMarkersTable,
  CustomMarkerRow,
  addDefinitionsToRows,
  carryForwardRowsFrom,
  refreshCarriedForwardFromLibrary,
  hasPractitionerInput,
  pickedByHandRows,
  customMarkersFromRows,
  emptyCustomMarkerRow,
  fillRowsFromImport,
  ImportedLabRow,
  ImportedRowsOutcome,
  isBlankCustomMarkerRow,
  moveRowWithin,
  rowKeysInUse,
  rowsFromCustomMarkers,
  validateCustomMarkerRows,
} from './custom-markers-table';

/*
 * The "these ratios are calculated for you" line under the Lipid Panel card.
 * Inline because this file imports no stylesheet of its own; sized and colored
 * like a form hint rather than a warning, since nothing is wrong and three
 * missing boxes are that card's normal state.
 */
const LIPID_RATIO_NOTE_STYLE: React.CSSProperties = {
  fontSize: '12px',
  color: '#6b645e',
  lineHeight: 1.4,
  marginTop: '4px',
};

/**
 * The fold key for the appendix card, the one every unfiled marker lands in.
 *
 * Read off sectionGroupKey rather than written out, so the appendix is keyed
 * here by the same rule that keys it everywhere else in this feature.
 */
export const APPENDIX_CARD_KEY = sectionGroupKey('');

/**
 * The card keys for a section order saved on a test, in that order.
 *
 * The order is stored as NAMES (see Bloodwork.customMarkerSectionOrder) and the
 * cards are keyed the way every other section-keyed thing in the app is, so
 * this is the one place the two spellings meet. Undefined for an order with
 * nothing in it, which is what bySectionOrder wants for "no opinion".
 */
export function sectionCardKeysFor(order?: string[]): string[] | undefined {
  const keys = (order || [])
    .map(name => (name || '').trim())
    .filter(Boolean)
    .map(name => sectionGroupKey(practitionerSectionOptionValue(name)));
  return keys.length ? keys : undefined;
}

const Spinner = require('react-spinkit');
// Served from the public/ folder (not bundled) so the app compiles even before
// the image file is added; the diagram lives at public/assets/imgs/menstrual-cycle-phases.jpg
const menstrualCycleDiagram =
  process.env.PUBLIC_URL + '/assets/imgs/menstrual-cycle-phases.jpg';

// The I³ entry card is parked until its design is finished — flip this back to
// true to show it again (it stays env-gated to local dev on top of this flag).
const SHOW_IMMUNE_INFLAMMATORY_CARD = false;

// The number to fall back to when eGFR cannot be calculated from the form.
const EGFR_FALLBACKS: { [limit: number]: number } = { 60: 61, 90: 91 };

/**
 * The DOM id of the card holding one practitioner section's markers, keyed by
 * its section group key — the same key practitionerMarkerSections() renders
 * each card under, so "IgG Allergens" and "igg  allergens" address one card.
 *
 * Exported because it is a contract between the card and whatever scrolls to
 * it, and the key carries spaces: the ids the analysis screen scrolls to do too
 * (`section-Thyroid Function`), and getElementById matches them exactly.
 */
export function markerSectionCardId(groupKey: string): string {
  return `custom-marker-section-${groupKey}`;
}

type Props = {
  hideEnterTestResults?: boolean;
  selectedClient?: Client;
  currentUser: User;
  currentUserProfile: UserProfile;
  currentClients: Client[];
  bloodwork?: Bloodwork;
  bloodworkRanges: BloodworkRange[];
  bloodworkEntryStore: BloodworkEntryStore;
  refreshBloodworks: () => void;
  loadClientBloodworks?: (clientId: string) => void;
  updateClient?: (client: Client) => Promise<Client>;
  /**
   * Persist an updated My Markers library. Called after a successful save when
   * the test introduced a custom marker the practitioner has not defined yet.
   */
  learnCustomMarkers?: (library: CustomMarkerDefinition[]) => Promise<any>;
  /**
   * Persist a report section the practitioner named from the Section dropdown,
   * so it is on the list for every client from then on.
   */
  createCustomMarkerSection?: (name: string) => Promise<any> | void;
  /** Correct a section's name in the practitioner's saved list. */
  renameCustomMarkerSection?: (from: string, to: string) => Promise<any> | void;
  history?: any;
  path?: any;
};

type State = {
  [key: string]: any;
  validationErrors: Map<string, string>;
  collectionDate: string;
  collectionDateForUI: Date;
  alerts: Array<any>;
  selectedClient: Client;
  clientId: string;
  disableSelectClient: boolean;
  /**
   * Whether anything has been typed into this form since it was last saved or
   * cleared. Drives the navigation guard only; nothing here is autosaved.
   */
  dirty: boolean;
  showUpgradeModal: boolean;
  showUploadPdfUpgradeModal: boolean;
  showTimeSavingFeaturesModal: boolean;
  hideEnterTestResults: boolean;
  panelTitle: string;
};

function ClientOption(props: any) {
  const { data, isSelected, isFocused, innerRef, innerProps, options } = props;
  const index = (options as any[]).findIndex((o: any) => o.value === data.value);
  let bg = index % 2 === 0 ? '#ffffff' : '#f8f9fa';
  if (isFocused) bg = '#e8f0fe';
  if (isSelected) bg = '#0d6efd';
  return (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        backgroundColor: bg,
        color: isSelected ? '#ffffff' : '#212529',
        padding: '3px 12px',
        fontSize: '13px',
        borderBottom: '1px solid #eeeeee',
        cursor: 'pointer',
        fontWeight: isSelected ? 600 : 400,
      }}
    >
      {data.label}
    </div>
  );
}

/**
 * Where the pre-filled custom markers came from, phrased for the banner above
 * the table.
 *
 * The client's own history only. Rows that arrived from the sections added to
 * every test are deliberately left unnamed: those sections are already on
 * screen, each with a card of its own carrying its name and its marker count,
 * so naming them again describes what the practitioner is looking at. A row
 * recovered from a previous test has no such card to point at, and that is the
 * one piece of provenance nothing else on the page carries. The banner still
 * appears for a defaulted panel (see the table), carrying the rule about blank
 * rows without the redundant sentence about where they came from.
 */
export function customMarkerSourceLabel(
  sourceDates: string[],
): string | undefined {
  if (!sourceDates.length) {
    return undefined;
  }
  return sourceDates.length === 1
    ? `this client\u2019s test on ${moment(sourceDates[0]).format('MM/DD/YYYY')}`
    : 'this client\u2019s previous tests';
}

export class BloodworkEntry extends React.Component<Props, State> {
  selectedClient: Client = this.props.selectedClient
    ? this.props.selectedClient
    : new Client();
  clientId: string = this.props.selectedClient
    ? this.props.selectedClient.id
    : undefined;

  state: State = {
    validationErrors: new Map<string, string>(),
    collectionDate: undefined,
    collectionDateForUI: undefined,
    alerts: [],
    selectedClient: this.selectedClient,
    clientId: this.clientId,
    disableSelectClient: false,
    wbcDifferentialUnits: WhiteBloodCellDifferentialUnitType.PERCENT,
    dirty: false,
    showUpgradeModal: false,
    showCustomMarkersUpgradeModal: false,
    showUploadPdfUpgradeModal: false,
    showTimeSavingFeaturesModal: false,
    showMenstrualPhaseInfo: false,
    hideEnterTestResults: this.props.hideEnterTestResults,
    panelTitle:
      this.props.bloodwork && this.props.bloodwork.isNew()
        ? 'Add Test Results'
        : 'Edit Test Results',
    bwUnits: this.selectedClient?.units || 'US',
    height: this.selectedClient?.height ?? '',
    weight: this.selectedClient?.weight ?? '',
    heightFt: this.selectedClient?.height != null
      ? String(MathUtils.cmToFtIn(this.selectedClient.height).ft)
      : '',
    heightIn: this.selectedClient?.height != null
      ? String(MathUtils.cmToFtIn(this.selectedClient.height).inches)
      : '',
    // Raw string rows for the "Add Your Own Markers to This Test" table.
    // Converted to Bloodwork.customMarkers in handleSubmit; never written to
    // the record in this form.
    customMarkerRows: [emptyCustomMarkerRow()],
    // Collection date the carried-forward rows came from, for the banner.
    customMarkerCarriedForwardFrom: undefined,
    // The order the section cards are in: set by dragging one, by a rename on
    // this sitting (see sectionOrderAfterRename), or seeded from the order
    // saved on the test being edited.
    sectionCardOrder: undefined,
    // Whether this test carries an order of its OWN, rather than following the
    // practitioner's My Sections list. True once a card has been dragged here,
    // and true from the start for a test that was saved arranged. It is what
    // decides whether the save stamps an order at all: a test nobody arranged
    // must go on following that list, so that reordering My Sections still
    // reaches it the way it always has.
    sectionOrderArranged: false,
    // The card being dragged by its handle, and the card it is currently over.
    sectionDragIndex: undefined,
    sectionDropIndex: undefined,
    // Which marker cards are folded shut, by card key. View state about reading
    // the screen, the same as the fold state inside the table: not saved with
    // the test, and every card opens expanded, because a panel carried forward
    // onto a test arrives with blank rows waiting for values and folding those
    // away on arrival hides work still to do.
    collapsedCards: {},
  };

  async componentDidMount() {
    console.log('BloodworkEntry....componentDidMount.....................');
    // this.props.bloodworkEntryStore.setCurrentBloodwork(new Bloodwork());
    // Scroll-to-top must run on mount as well as componentWillReceiveProps:
    // when navigating in from the Home page Edit button, the bloodwork prop
    // is already set on the store before the route change, so props don't
    // change on mount and componentWillReceiveProps may never fire if all
    // container data is already cached.
    this.scrollToAddTestResultsTop();
    // Nothing on this form is autosaved, and a whole panel is a long sit. The
    // browser's own warning covers closing the tab and reloading; the <Prompt>
    // in render covers navigating inside the app.
    window.addEventListener('beforeunload', this.warnIfUnsaved);
    // Before any client is chosen: the practitioner's panels are a property of
    // THEM, not of the client, so an empty screen here reads as a broken
    // feature.
    this.loadDefaultCustomMarkers();
    const showTimeSavingFeaturesModal =
      await this.displayTimeSavingFeaturesModal();
    if (showTimeSavingFeaturesModal) {
      this.setState({
        showTimeSavingFeaturesModal,
      });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.warnIfUnsaved);
  }

  /**
   * The browser's leave-this-page prompt, while there is work on the form.
   *
   * Every modern browser ignores the string and shows its own wording, so the
   * only job here is to set returnValue at all.
   */
  private warnIfUnsaved = (event: BeforeUnloadEvent) => {
    if (!this.state.dirty) {
      return undefined;
    }
    event.preventDefault();
    event.returnValue = '';
    return '';
  };

  componentDidUpdate(previousProps: Props) {
    // MobX may hand the profile over after the first render, and the defaults
    // live on it. Guarded by hasPractitionerInput inside, so a later profile
    // refresh cannot reload rows over work in progress.
    if (previousProps.currentUserProfile !== this.props.currentUserProfile) {
      this.loadDefaultCustomMarkers();
    }
  }

  componentWillReceiveProps(nextProps: Props) {
    const bloodworkData: any = {};
    const { history, hideEnterTestResults } = nextProps;
    let bloodwork = new Bloodwork(nextProps.bloodwork);
    // Only scroll to the top when the record being edited actually changes
    // (a genuine "load this record into the form" navigation event). The
    // container is a MobX observer that re-renders as clients, ranges, the
    // user profile, and further-considerations finish loading; scrolling on
    // every one of those incidental prop updates yanks the user back to the
    // top repeatedly while the page is still settling.
    if (nextProps.bloodwork !== this.props.bloodwork) {
      this.scrollTop();
    }
    this.handleNumberOfTestsLimit();

    // setup needed if we are editing bloodwork
    if (bloodwork && !bloodwork.isNew()) {
      console.log('We have bloodwork and its not new.....');

      // Check to see if we came thru the "add" route
      if (this.isAddMode(history)) {
        for (let key of Object.keys(bloodwork)) {
          bloodworkData[key] = '';
        }
        this.hydratedBloodworkId = undefined;
        this.clientResolvedBloodworkId = undefined;
        this.setState({
          ...bloodworkData,
          hideEnterTestResults: false,
          panelTitle: 'Add Test Results',
          wbcDifferentialUnits: WhiteBloodCellDifferentialUnitType.PERCENT,
          // The blanket `= ''` above would leave the custom-marker table with a
          // string where it expects an array.
          customMarkerRows: [emptyCustomMarkerRow()],
          customMarkerCarriedForwardFrom: undefined,
          sectionCardOrder: undefined,
          sectionOrderArranged: false,
          // A drag in flight belongs to the test that was on screen. This
          // lifecycle runs on every incidental store update, so one can be in
          // flight while a record is re-seeded underneath it, and the drop that
          // followed would move a card on the test that just arrived.
          sectionDragIndex: undefined,
          sectionDropIndex: undefined,
          collapsedCards: {},
        });
        return;
      }

      const collectionDateForUI = moment(bloodwork.collectionDate).toDate();

      // Fall back to the client's profile values for height/weight/units
      // when the bloodwork itself doesn't have them. Prefer the live client
      // record over bloodwork.client, since the snapshot saved with the test
      // can predate height/weight being added to the client.
      const liveClient = bloodwork.clientId && nextProps.currentClients
        ? Client.findClient(bloodwork.clientId, nextProps.currentClients)
        : undefined;
      const bwClient = liveClient || bloodwork.client;
      const effectiveHeightCm = (bloodwork.height != null && bloodwork.height !== ('' as any))
        ? Number(bloodwork.height)
        : (bwClient?.height ?? null);
      const effectiveWeightLbs = (bloodwork.weight != null && bloodwork.weight !== ('' as any))
        ? Number(bloodwork.weight)
        : (bwClient?.weight ?? null);
      const effectiveUnits: 'US' | 'SI' = bwClient?.units === 'SI' ? 'SI' : 'US';
      const { ft: effFt, inches: effIn } = effectiveHeightCm != null
        ? MathUtils.cmToFtIn(effectiveHeightCm)
        : { ft: '', inches: '' };

      // This ran unconditionally, and this lifecycle runs on every incidental
      // re-render (the container observes the profile, the ranges, the client
      // list, the considerations and the test, and re-renders as each lands).
      // So opening one saved test re-seeded every field from the record five
      // times over — and once anything HAD been typed, the next store update
      // underneath the form quietly put the saved value back. Correcting a
      // glucose of 88 to 95 and then loading more test results in the list
      // below left 95 on screen for as long as it took the store to answer.
      const recordChanged = this.hydratedBloodworkId !== bloodwork.id;
      // The client list usually lands after the record, and height, weight and
      // units are read off the LIVE client. So they get a second chance, once,
      // when a real client record first becomes available. A record change is a
      // fresh load and always applies; the late top-up only applies while
      // nothing has been typed, so it can never overwrite a height just
      // entered.
      const clientNowResolvable =
        !!liveClient && this.clientResolvedBloodworkId !== bloodwork.id;
      const applyClientFields =
        recordChanged || (clientNowResolvable && !this.state.dirty);

      // Mirrors the store rather than the record, so it follows its prop.
      if (hideEnterTestResults !== this.state.hideEnterTestResults) {
        this.setState({ hideEnterTestResults });
      }

      if (recordChanged) {
        this.hydratedBloodworkId = bloodwork.id;
        // Inside the guard on purpose: it rewrites the differentials in place,
        // and it was running once per re-render on the same record.
        bloodwork.convertWBCDifferentials(bloodwork.wbcDifferentialUnits);
        for (let key of Object.keys(bloodwork)) {
          bloodworkData[key] = bloodwork[key];
        }
        this.setState({
          ...bloodworkData,
          panelTitle: 'Edit Test Results',
          disableSelectClient: true,
          collectionDateForUI: collectionDateForUI,
          customMarkerRows: rowsFromCustomMarkers(bloodwork.customMarkers),
          customMarkerCarriedForwardFrom: undefined,
          // The order this test was saved arranged in, if it was. Seeded rather
          // than cleared, so reopening a test shows the cards the way the
          // client's report prints them, and so a save from here cannot quietly
          // flatten an arrangement made on an earlier sitting.
          sectionCardOrder: sectionCardKeysFor(
            bloodwork.customMarkerSectionOrder,
          ),
          sectionOrderArranged: !!sectionCardKeysFor(
            bloodwork.customMarkerSectionOrder,
          ),
          // Same reason as the add path above: a drag in flight belongs to the
          // test that was on screen when it started.
          sectionDragIndex: undefined,
          sectionDropIndex: undefined,
          collapsedCards: {},
          // A record just loaded is a form with nothing unsaved on it.
          dirty: false,
        });
      }

      if (applyClientFields) {
        if (liveClient) {
          this.clientResolvedBloodworkId = bloodwork.id;
        }
        this.setState({
          selectedClient: bwClient,
          bwUnits: effectiveUnits,
          height: effectiveHeightCm ?? '',
          weight: effectiveWeightLbs ?? '',
          heightFt: effectiveHeightCm != null ? String(effFt) : '',
          heightIn: effectiveHeightCm != null ? String(effIn) : '',
        });
      }

      // Editing a test that has no custom markers of its own: offer the
      // client's existing ones so they can be back-filled here too. Excludes
      // this record, and carryForwardCustomMarkers bails if anything has been
      // typed, so a test that already has markers is never touched.
      if (
        recordChanged &&
        (!Array.isArray(bloodwork.customMarkers) ||
          bloodwork.customMarkers.length === 0)
      ) {
        this.carryForwardCustomMarkers(
          bloodwork.clientId,
          bloodwork.id,
          bwClient?.gender,
        );
      }
    } else {
      console.log('inside else in componentWillReceiveProps....');
      const preselected = nextProps.selectedClient;
      // Only (re)hydrate the client-derived fields when the container actually
      // hands us a *different real* preselected client — i.e. navigating in
      // from the Clients table "Add Test Results" button (/bloodwork/add/:id).
      // On the plain /bloodwork/add route there is no preselected client, so
      // the container passes a fresh empty `new Client()` on every render. This
      // lifecycle runs on every incidental MobX re-render (clients, ranges,
      // profile, further-considerations finishing loading), so blindly resetting
      // selectedClient here would clobber a client the user just picked from the
      // dropdown — which hides the conditional menstrual-phase field for
      // premenopausal females.
      const preselectedId = preselected?.id;
      const preselectedChanged = preselectedId !== this.props.selectedClient?.id;
      if (preselectedId && preselectedChanged) {
        const heightCm = preselected?.height;
        const clientUnits = preselected?.units || 'US';
        const { ft, inches } = heightCm != null
          ? MathUtils.cmToFtIn(heightCm)
          : { ft: '', inches: '' };
        this.setState({
          hideEnterTestResults,
          panelTitle: 'Add Test Results',
          wbcDifferentialUnits: WhiteBloodCellDifferentialUnitType.PERCENT,
          selectedClient: preselected,
          bwUnits: clientUnits,
          height: heightCm ?? '',
          weight: preselected?.weight ?? '',
          heightFt: heightCm != null ? String(ft) : '',
          heightIn: heightCm != null ? String(inches) : '',
        });
        // Arrived from the Clients table's "Add Test Results" — same carry
        // forward as picking the client from the dropdown.
        this.carryForwardCustomMarkers(
          preselectedId,
          undefined,
          preselected?.gender,
        );
      } else {
        this.setState({
          hideEnterTestResults,
          panelTitle: 'Add Test Results',
          wbcDifferentialUnits: WhiteBloodCellDifferentialUnitType.PERCENT,
        });
      }
    }
  }

  isAddMode(history: any) {
    return (
      history &&
      history.location &&
      history.location.pathname === '/bloodwork/add'
    );
  }

  /**
   * True when the form is editing an existing (already-saved) test rather than
   * adding a new one. Derived from props so it does NOT depend on
   * componentWillReceiveProps having fired — when navigating in from the Home
   * page Edit button the bloodwork prop is already set before the route change,
   * so that lifecycle (and the disableSelectClient state it sets) can be skipped
   * entirely. The client must never be changed on an existing test, so the
   * client dropdown is disabled whenever this is true.
   */
  private isEditingExistingTest(): boolean {
    const { bloodwork, history } = this.props;
    return (
      !!bloodwork &&
      typeof bloodwork.isNew === 'function' &&
      !bloodwork.isNew() &&
      !this.isAddMode(history)
    );
  }

  /**
   * Whether saving this form would add a test the plan does not allow.
   *
   * False while editing a test that already exists: that save replaces a record
   * already counted, so it neither adds to the total nor needs an upgrade.
   */
  private blockedByTestLimit(): boolean {
    const { currentUserProfile } = this.props;
    return (
      !this.isEditingExistingTest() &&
      !!currentUserProfile &&
      !currentUserProfile.canAddTest()
    );
  }

  handleNumberOfTestsLimit() {
    if (this.blockedByTestLimit()) {
      this.setState({ showUpgradeModal: true });
    }
  }

  handleUploadPdfLimit() {
    const { currentUserProfile } = this.props;
    if (currentUserProfile && !currentUserProfile.canUploadPdf()) {
      this.setState({ showUploadPdfUpgradeModal: true });
    }
  }

  handleInputChange = (event: any) => {
    // console.log(event ? event.target : 'event was falsey...');
    if (!this.state.dirty) {
      this.setState({ dirty: true });
    }
    const fieldId = event.target.name;
    let value = event.target.value;

    // Convert empty string to undefined so our automatic calculations work correctly
    if (value.trim().length === 0) {
      value = undefined;
    }

    // If the wbcDifferentialUnits selection changes then clear out wbc differencial inputs
    if (fieldId === 'wbcDifferentialUnits') {
      this.setState({
        [fieldId]: value,
        neutrophils: '',
        lymphocytes: '',
        monocytes: '',
        eosinophils: '',
        basophils: '',
      });
    } else {
      this.setState({
        [fieldId]: value,
      });
    }

    if (fieldId === 'clientId') {
      this.handleClientChange(event.target.value);
    }

    this.clearAlerts();
  };

  handleClientChange = (clientId: string) => {
    const client = Client.findClient(clientId, this.props.currentClients);
    const heightCm = client?.height;
    const clientUnits = client?.units || 'US';
    const { ft, inches } = heightCm != null
      ? MathUtils.cmToFtIn(heightCm)
      : { ft: '', inches: '' };
    this.setState({
      selectedClient: client || new Client(),
      bwUnits: clientUnits,
      height: heightCm ?? '',
      weight: client?.weight ?? '',
      heightFt: heightCm != null ? String(ft) : '',
      heightIn: heightCm != null ? String(inches) : '',
    });
    this.carryForwardCustomMarkers(clientId, undefined, client?.gender);
  };

  /**
   * Pre-fill the custom-marker table with every custom marker this client has
   * ever had, so a repeat panel only needs its values typed rather than each
   * marker defined again.
   *
   * Gathered across the client's WHOLE history, not just their latest test: a
   * marker run in January and skipped in March should still be one keystroke
   * away in June. Markers are keyed by name, and the most recent definition of
   * each wins, so a range the practitioner has since corrected is the one that
   * carries forward.
   *
   * Deliberately silent on failure: this is a convenience, and a client with no
   * prior custom markers (or a lookup that fails) should just leave the table
   * empty rather than surface an error on an unrelated field.
   */
  /**
   * Load the practitioner's default sections and markers, with NO client
   * needed.
   *
   * These used to wait for a client to be picked, because per-sex ranges cannot
   * be resolved without knowing whose test it is. That was the wrong trade: a
   * practitioner opens Add Test Results, sees none of the panels they set up,
   * and writes to support saying the feature is broken. The ranges are resolved
   * again the moment a client IS picked (see carryForwardCustomMarkers), so the
   * only cost of showing them early is that a sex-specific range shows its
   * default until then.
   *
   * Runs on mount and again when the profile arrives, since MobX may deliver it
   * after the first render.
   */
  loadDefaultCustomMarkers = () => {
    const profile = this.props.currentUserProfile;
    if (!profile) {
      return;
    }
    const defaults = definitionsForDefaults(
      profile.customMarkerLibrary,
      profile.defaultTestSections,
      profile.defaultTestMarkerIds,
    );
    if (!defaults.length) {
      return;
    }
    // Never over anything the practitioner has typed, and never a second time
    // over rows this already put there.
    const rows: CustomMarkerRow[] = this.state.customMarkerRows || [];
    if (hasPractitionerInput(rows)) {
      return;
    }
    const next = addDefinitionsToRows(
      rows,
      defaults,
      this.state.selectedClient?.gender,
    );
    if (next === rows) {
      return;
    }
    // No source label: the sections these came from are on screen already as
    // cards of their own. The table still shows the short banner off the rows'
    // own carriedForward flag.
    this.setState({
      customMarkerRows: next,
      customMarkerCarriedForwardFrom: undefined,
    });
  };

  /**
   * The client whose carry-forward is already running.
   *
   * componentWillReceiveProps fires on every incidental MobX re-render (the
   * container observes the profile, the ranges, the client list, the further
   * considerations and the test, and re-renders as each one lands), and on the
   * edit path it calls carryForwardCustomMarkers every time. The cache check
   * inside only helps once a load has come BACK, so opening one saved test
   * fired a full load of the client's entire history five times over, five
   * concurrent round trips each toggling the global busy spinner — which is
   * a boolean, so the first one to finish cleared it while the rest ran on.
   *
   * Keyed by client rather than a bare boolean so genuinely switching client
   * is never the thing that gets swallowed.
   */
  private carryForwardInFlight?: string;

  /**
   * The record already loaded into the form, and the one whose height and
   * weight have been resolved against a LIVE client record.
   *
   * Deliberately NOT a prop-identity comparison. currentBloodwork is set on the
   * store before this screen mounts, so the bloodwork prop is the same object on
   * every one of the re-renders below; gating on identity would mean the form
   * never filled in at all. What matters is whether THIS record has been loaded
   * yet, which is a question about the record, not about the reference.
   */
  private hydratedBloodworkId?: string;
  private clientResolvedBloodworkId?: string;

  carryForwardCustomMarkers = async (
    clientId: string,
    excludeBloodworkId?: string,
    // Passed in rather than read off state: every caller setState()s the newly
    // picked client in the same React batch as this call, and the cached-tests
    // path never awaits, so reading this.state.selectedClient here would
    // resolve per-sex ranges against the PREVIOUS client (or none at all).
    clientGender?: string,
  ) => {
    if (!clientId || this.carryForwardInFlight === clientId) {
      return;
    }
    this.carryForwardInFlight = clientId;
    try {
      await this.runCarryForwardCustomMarkers(
        clientId,
        excludeBloodworkId,
        clientGender,
      );
    } finally {
      this.carryForwardInFlight = undefined;
    }
  };

  private runCarryForwardCustomMarkers = async (
    clientId: string,
    excludeBloodworkId?: string,
    clientGender?: string,
  ) => {
    // Never clobber work in progress — only fill a table the user hasn't
    // typed into. Re-picking a client after typing keeps what they typed.
    //
    // "Anything typed" rather than hasPractitionerInput, because markers picked
    // by hand from My Markers are carried ACROSS the rebuild below rather than
    // blocking it: the practitioner asked for those, and the client's own
    // history should still load around them.
    const rows: CustomMarkerRow[] = this.state.customMarkerRows || [];
    const picked = pickedByHandRows(rows);
    if (rows.some(row => (row.value || '').trim())) {
      return;
    }

    let history: CustomMarkerRow[] = [];
    let sourceDates: string[] = [];

    try {
      const { bloodworkEntryStore } = this.props;
      // The store already holds this client's tests whenever we arrived from
      // their row or are editing one of their tests; only fetch otherwise, so
      // picking a client from the dropdown doesn't flash the loading spinner
      // for nothing.
      const cached = bloodworkEntryStore.clientBloodworks || [];
      const bloodworks =
        cached.length && cached[0].clientId === clientId
          ? cached
          : await bloodworkEntryStore.loadClientBloodworks(clientId);

      // clientBloodworks is ordered newest first, which is what makes the most
      // recent definition of each marker win.
      const gathered = gatherCustomMarkersAcrossTests(bloodworks || [], {
        excludeTestId: excludeBloodworkId,
      });
      history = carryForwardRowsFrom(gathered.markers);
      sourceDates = gathered.sourceDates;
    } catch (error) {
      // Deliberately silent: this is a convenience, and a lookup that fails
      // should still let the practitioner's default sections load rather than
      // surface an error on an unrelated field.
      console.error('Could not carry forward custom markers', error);
    }

    // Guard again: the fetch is async and the user may have started typing.
    const current: CustomMarkerRow[] = this.state.customMarkerRows || [];
    if (current.some(row => (row.value || '').trim())) {
      return;
    }

    // The client's own history first, then the sections set to load onto every
    // test, deduped by marker name. Union rather than either-or: history is
    // what this client actually ran and must never be dropped, and a panel
    // added to the defaults afterwards has to reach an existing client too.
    //
    // Built fresh from `history` rather than from what is on screen, so the
    // rows this client's sex actually calls for replace whatever was resolved
    // before anyone was chosen. Nothing is lost: hasPractitionerInput above has
    // already established that the only rows there are ones we put there.
    const profile = this.props.currentUserProfile;
    const defaults = definitionsForDefaults(
      profile?.customMarkerLibrary,
      profile?.defaultTestSections,
      profile?.defaultTestMarkerIds,
    );
    const gender = clientGender ?? this.state.selectedClient?.gender;
    // History says WHICH markers this client runs. My Markers says what each
    // one is. Before this, history answered both, so a range corrected in the
    // library reached a new client and a marker this client had never run, and
    // nothing else — the next test for anyone who had already had the marker
    // inherited the old numbers from their last one. See
    // refreshCarriedForwardFromLibrary.
    const refreshed = refreshCarriedForwardFromLibrary(
      history,
      profile?.customMarkerLibrary,
      gender,
    );
    const rebuilt = addDefinitionsToRows(refreshed, defaults, gender);
    // The picks go on last and keep their flag, so a second client change does
    // not lose them either. addDefinitionsToRows dedupes by name, so one the
    // history or the defaults already brought in is not added twice.
    const library = this.props.currentUserProfile?.customMarkerLibrary || [];
    const pickedDefinitions = picked
      .map(row =>
        library.find(
          definition =>
            CustomMarker.keyFor(definition.name) ===
            CustomMarker.keyFor(row.name),
        ),
      )
      .filter(Boolean) as CustomMarkerDefinition[];
    const next = addDefinitionsToRows(rebuilt, pickedDefinitions, gender, {
      byHand: true,
    });
    if (!next.length) {
      return;
    }

    this.setState({
      customMarkerRows: next,
      customMarkerCarriedForwardFrom: customMarkerSourceLabel(sourceDates),
    });
  };

  /**
   * The practitioner's own sections that have markers on this test, each
   * getting a card of its own above the catch-all.
   *
   * Shipped sections are deliberately excluded: the form already has a card
   * headed "Glucose Regulation & Metabolic Function", and a second one for the
   * two markers filed there would be two cards with one name. Those stay in the
   * catch-all card under their heading, where the heading is the only thing
   * distinguishing them.
   */
  practitionerMarkerSections(): {
    key: string;
    label: string;
    value: string;
    count: number;
  }[] {
    const rows: CustomMarkerRow[] = this.state.customMarkerRows || [];
    const groups = groupBySectionValue(
      rows.map((row, index) => ({ row, index })),
      entry => entry.row.section,
      this.props.currentUserProfile?.customMarkerSections,
    )
      .filter(group => group.kind === 'practitioner')
      .map(group => ({
        key: sectionGroupKey(group.value),
        label: group.label,
        value: group.value,
        count: group.items.length,
      }));
    // In the order My Sections sets, unless this test has an order of its own:
    // one dragged here, one it was saved with, or one a rename on this sitting
    // pinned (see sectionOrderAfterRename). The pin still earns its place even
    // among the other two: a section the practitioner's list does not name
    // sorts alphabetically among the other unlisted ones, so renaming one of
    // those still moves its card.
    return bySectionOrder(groups, this.state.sectionCardOrder);
  }

  /**
   * Move one section card into another's place, for THIS test only.
   *
   * The order is the order the sections print in on the analysis screen, in the
   * comparison table and in both PDFs — the same thing dragging My Sections
   * sets, except that this one is saved on the test and so applies to this
   * client's report alone. Which is the whole reason it exists: a panel that
   * should lead one client's report is not necessarily the one that should lead
   * every client's.
   *
   * moveRowWithin does the arithmetic, over the slots the cards occupy, which
   * for a flat list of cards is every slot. It is the helper the marker rows
   * and My Sections' rows already move through, and it returns the same array
   * when nothing moved — so an unchanged drop sets no state and, importantly,
   * does not mark the test arranged.
   */
  private moveSectionCard = (fromIndex: number, toIndex: number) => {
    const keys = this.practitionerMarkerSections().map(group => group.key);
    const next = moveRowWithin(
      keys,
      keys.map((_, index) => index),
      fromIndex,
      toIndex,
    );
    if (next === keys) {
      return;
    }
    this.setState({
      sectionCardOrder: next,
      // The drag IS the practitioner saying this test has its own order. Until
      // one happens the test keeps following My Sections, however many times it
      // is saved.
      sectionOrderArranged: true,
      dirty: true,
    });
  };

  /** The handle's arrow keys, in the units the handle is in: one card. */
  private moveSectionCardBy = (index: number, step: number) => {
    const count = this.practitionerMarkerSections().length;
    const target = index + step;
    if (target < 0 || target >= count) {
      return;
    }
    this.moveSectionCard(index, target);
  };

  /**
   * Every card that can be folded, in the order they are on screen: the
   * practitioner's sections, then the appendix.
   *
   * The appendix is in the list although it cannot be dragged. Folding is about
   * the length of the page, and it gets as long as any panel.
   */
  private collapsibleCardKeys(): string[] {
    return [
      ...this.practitionerMarkerSections().map(group => group.key),
      APPENDIX_CARD_KEY,
    ];
  }

  /** True when every card is folded, which is what flips the control's label. */
  private allCardsCollapsed(): boolean {
    return this.collapsibleCardKeys().every(
      key => !!this.state.collapsedCards[key],
    );
  }

  /**
   * Fold or unfold one card.
   *
   * Never marks the form dirty: which cards are open is about reading the
   * screen, not about the results on it, and a navigation guard that fires
   * because somebody folded a panel to look past it is a guard nobody believes.
   */
  private toggleCard = (key: string) => {
    this.setState(state => ({
      collapsedCards: {
        ...state.collapsedCards,
        [key]: !state.collapsedCards[key],
      },
    }));
  };

  /**
   * Fold or unfold all of them at once.
   *
   * This is the control that makes reordering work at any size. Folded, the
   * cards are a short run of headings that fits on one screen, so moving a panel
   * past a forty-marker urinalysis is a short drag instead of a drag with a
   * scroll held open underneath it.
   */
  private setAllCardsCollapsed = (collapsed: boolean) => {
    const collapsedCards: { [key: string]: boolean } = {};
    if (collapsed) {
      this.collapsibleCardKeys().forEach(key => {
        collapsedCards[key] = true;
      });
    }
    this.setState({ collapsedCards });
  };

  private onSectionDragStart = (
    event: React.DragEvent<HTMLElement>,
    index: number,
  ) => {
    // Firefox drags nothing at all without payload data set.
    event.dataTransfer.effectAllowed = 'move';
    try {
      event.dataTransfer.setData('text/plain', String(index));
    } catch (error) {
      // IE-ism; the drag still works off component state below.
    }
    this.setState({ sectionDragIndex: index, sectionDropIndex: undefined });
  };

  /**
   * Guarded on a card drag being in progress, which is what keeps the two
   * drags apart. The marker rows inside these cards are draggable too and their
   * drops bubble up to here; without this guard, reordering a row in one card
   * would move the CARD it was dropped in.
   */
  private onSectionDragOver = (
    event: React.DragEvent<HTMLElement>,
    index: number,
  ) => {
    if (this.state.sectionDragIndex === undefined) {
      return;
    }
    // Without this the drop never fires: the default handling of a dragover is
    // to refuse the drop.
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    // Only on a change. A dragover fires continuously, and this state sits on
    // the entry form — the largest component in the app — so setting it per
    // event would re-render every input on the page per pixel moved.
    if (this.state.sectionDropIndex !== index) {
      this.setState({ sectionDropIndex: index });
    }
  };

  private onSectionDrop = (
    event: React.DragEvent<HTMLElement>,
    index: number,
  ) => {
    const from = this.state.sectionDragIndex;
    if (from === undefined) {
      return;
    }
    event.preventDefault();
    this.setState({ sectionDragIndex: undefined, sectionDropIndex: undefined });
    this.moveSectionCard(from, index);
  };

  private onSectionDragEnd = () => {
    this.setState({ sectionDragIndex: undefined, sectionDropIndex: undefined });
  };

  /**
   * Where the dragged card would land, drawn on the card it would land against.
   *
   * My Sections' classes, because it is the same drawing and the same insets:
   * a border would change the card's height as the pointer crossed it and slide
   * every card below out from under the drop.
   */
  private sectionCardDropClass(index: number): string {
    const { sectionDragIndex, sectionDropIndex } = this.state;
    if (sectionDragIndex === undefined) {
      return '';
    }
    if (sectionDragIndex === index) {
      return 'custom-marker-line-dragging';
    }
    if (sectionDropIndex !== index) {
      return '';
    }
    return sectionDragIndex < index
      ? 'custom-marker-line-drop-below'
      : 'custom-marker-line-drop-above';
  }

  /**
   * Save a section named from the Section dropdown to the practitioner's
   * profile. The marker keeps the name whatever happens here — it is stored on
   * the test, not looked up — so a failure costs them the section on the NEXT
   * client's dropdown, and that is what the warning says.
   */
  createSection = (name: string) => {
    // Naming a section here moves the row out of the card it was typed in and
    // into a card of its own, and that card is inserted ABOVE the appendix
    // card. The browser holds the scroll offset while everything below the new
    // card slides down by its height, so the practitioner is left looking at
    // whatever now happens to sit at that offset — markers they were not
    // working on, several cards up. The new card is the thing they just asked
    // for, so go to it.
    //
    // Fired before the section is saved to the profile on purpose: the card is
    // drawn off the row's own section (SectionSelect calls onChange straight
    // after this), so it is on screen whether or not the profile write lands.
    // The helper waits a frame, which is what the row move needs.
    //
    // ...UntilItLands, because saving the section to the profile puts the app's
    // spinner modal up over the page, and nothing can scroll while a modal is
    // mounted. A one-shot scroll therefore worked only when the name typed was
    // already on the practitioner's list — nothing to save, nothing in the way
    // — and did nothing at all for the new section they had just named, which
    // is the whole point of the jump.
    scrollPanelIntoViewUntilItLands(
      markerSectionCardId(
        sectionGroupKey(practitionerSectionOptionValue(name)),
      ),
    );
    const created = this.props.createCustomMarkerSection?.(name);
    if (created && typeof created.catch === 'function') {
      created.catch((err: any) => {
        console.error('Failed to save the new section', err);
        this.addAlert(
          {
            type: 'warning',
            text: profileSaveErrorMessage(
              err,
              `“${name}” is set on this marker and saves with the test, but it could not be added to your section list for other clients. You can add it under Test Results, My Markers.`,
            ),
          },
          true,
        );
      });
    }
  };

  /**
   * Correct a section's name in the practitioner's list. The rows on this test
   * are renamed by the table itself; tests already saved keep the name they
   * were saved with until they are opened, which is what stops a rename from
   * retitling a section on a report already given to a client.
   */
  renameSection = (from: string, to: string) => {
    // Read BEFORE the rename lands, so the pin is the order on screen right
    // now: the card the practitioner is working in keeps the slot it has,
    // rather than re-sorting to wherever its new name falls among the sections
    // their list does not order.
    const pinned = sectionOrderAfterRename(
      this.practitionerMarkerSections().map(group => group.key),
      sectionGroupKey(practitionerSectionOptionValue(from)),
      sectionGroupKey(practitionerSectionOptionValue(to)),
    );
    if (pinned) {
      this.setState({ sectionCardOrder: pinned });
    }
    const renamed = this.props.renameCustomMarkerSection?.(from, to);
    if (renamed && typeof renamed.catch === 'function') {
      renamed.catch((err: any) => {
        console.error('Failed to rename the section', err);
        this.addAlert(
          {
            type: 'warning',
            text: profileSaveErrorMessage(
              err,
              `The markers on this test now read “${to}”, but your saved section list still says “${from}”. You can rename it under Test Results, My Markers.`,
            ),
          },
          true,
        );
      });
    }
  };

  handleOnBlur = (event: any) => {
    this.handleCalculatedRatioFields(event.target.name);
    this.handleGlobulinCalculation(event.target.name);
    this.handleMeanCorpuscularVolumeCalculation(event.target.name);
    this.handleMeanCorpuscularHGBCalculation(event.target.name);
    this.handleMCHConcentrationCalculation(event.target.name);
    this.handleBilirubinCalculation(event.target.name);
    this.handleVldlCalculation(event.target.name);
    this.handleSaturationCalculation(event.target.name);
    this.handleTestosteroneBioavailableCalculation(event.target.name);
    this.handleLessThanCalculation(event.target.name);
  };

  // Each ratio gets its own `if`, deliberately not an else-if chain: HDL is a
  // component of BOTH Cholesterol/HDL and Triglycerides/HDL, so a chain stops at
  // the first match and leaves the second ratio stale. That bit TG/HDL — it only
  // repopulated when Triglycerides was blurred, so entering Triglycerides before
  // HDL left it blank. TG/HDL is one of the three primary Glucose markers, so a
  // blank there silently shifts the Glucose gauge and pattern.
  handleCalculatedRatioFields(fieldId: string) {
    // EVERY ratio the blurred field takes part in, not the first. See
    // ratiosInvolving, which carries the reason and the test.
    ratiosInvolving(fieldId).forEach(ratio =>
      this.handlePopulateRatio(ratio.numerator, ratio.denominator, ratio.field),
    );
  }

  handlePopulateRatio(
    numerator: string,
    denominator: string,
    ratioField: string,
  ) {
    // deriveRatio answers `undefined` for a missing or zero component rather
    // than a number, which is what keeps a ratio the lab never reported empty:
    // Number('') is 0, and 0 reads as *optimal* for TG/HDL.
    const ratio = deriveRatio(this.state[numerator], this.state[denominator]);

    // Clearing is this form's job, not the formula's. A stale ratio computed
    // from the *old* HDL is worse than a visibly empty field, and the server
    // has no equivalent: it sees one payload and never a replaced value.
    this.setState({ [ratioField]: ratio === undefined ? '' : ratio });
  }

  /**
   * A field's value as a number, or NaN when the field is empty.
   *
   * `Number('')` is 0, not NaN, and that is the whole trap. Every calculated
   * field below guards its inputs with isNaN, so an empty field used to pass
   * that guard as a zero and then take part in the arithmetic: divided into,
   * it produced Infinity and rendered as "NaN"; subtracted, it produced a
   * plausible wrong number instead. A field the lab did not report has to reach
   * these calculations as NaN so the guard actually stops them.
   */
  numericField(value: any): number {
    return numericValue(value);
  }

  /**
   * VLDL cholesterol from the three components, when the lab did not print it.
   *
   * Three `if`s worth of components in one guard rather than a chain, for the
   * same reason handleCalculatedRatioFields gives: cholesterol, HDL and LDL are
   * each a component, and whichever is blurred has to recompute the whole thing.
   *
   * A PRINTED VLDL WINS, which is why this goes through the same
   * `indexIsOpenToDerivation` tracker MCV / MCH / MCHC use rather than simply
   * overwriting. 49 of the corpus reports print this row across six labs, and
   * `vLDL` carries aliases for all of their spellings, so the common case is
   * that the field is already filled from the report before anything is
   * blurred. Overwriting it with the subtraction would replace the lab's own
   * number with one computed off the lab's own LDL-C, which may itself be the
   * NIH/Sampson calculation — close, and not the same.
   *
   * So: fill when the field is empty, redo when it still holds exactly what
   * this session's arithmetic put there, and otherwise leave it alone. The
   * import path has the same ordering through `deriveMissing`, where `fill`
   * never overwrites a measured value.
   */
  handleVldlCalculation(fieldId: string) {
    if (fieldId !== 'cholesterol' && fieldId !== 'hdl' && fieldId !== 'ldl') {
      return;
    }
    // No box to fill in prod (vldlVisibility.ts).
    if (!vldlVisible()) {
      return;
    }
    if (!this.indexIsOpenToDerivation('vLDL')) {
      return;
    }
    const vldl = deriveVldl(this.state.cholesterol, this.state.hdl, this.state.ldl);
    if (vldl === undefined) {
      // A component was cleared, so whatever this calculation last wrote is
      // now unfounded. Clearing it is the same judgement handlePopulateRatio
      // makes: an empty field beats a number derived from an old value.
      this.setState({ vLDL: '' });
      return;
    }
    this.setDerivedIndex('vLDL', vldl);
  }

  handleGlobulinCalculation(fieldId: string) {
    if (fieldId === 'protein' || fieldId === 'albumin') {
      const globulin = deriveGlobulin(this.state.protein, this.state.albumin);
      if (globulin !== undefined) {
        this.setState({ globulin });
      }
    }
  }

  /**
   * What the three red-cell index calculations below last wrote, by field.
   *
   * MCV, MCH and MCHC used to be recomputed on every blur of an input, so a
   * measured value could never survive: the parser fills the field from the
   * report and the import then blurs hematocrit and RBC, overwriting it before
   * the practitioner ever sees it. That is backwards. A modern hematology
   * analyzer MEASURES MCV directly by impedance and DERIVES the hematocrit from
   * MCV x RBC, so the lab's printed index is the measured number and the
   * arithmetic below is a back-calculation of something the analyzer never
   * measured. Where the report or the practitioner has given us a value, it
   * wins, and these calculations fill the gap rather than overrule it.
   *
   * Fill-if-empty on its own would go too far the other way. A practitioner who
   * mistypes hematocrit and corrects it would be left with an MCV derived from
   * the typo, because the field is no longer empty. So this records the value
   * the arithmetic itself put on screen, and a field is recomputed when it is
   * empty OR still holds exactly that. Anything else came from the report or
   * from a keystroke, whichever route it took, and is left alone. Recording the
   * VALUE rather than a "derived" flag is what makes that true of every route
   * at once: a second import, or a paid read, lands a new number in state and
   * it simply stops matching.
   *
   * An instance field rather than state on purpose. It drives no rendering, and
   * it is scoped to this editing session: a test reopened later carries no
   * provenance, so whatever was saved is treated as measured and left alone.
   */
  private lastDerivedIndex: { [field: string]: string } = {};

  /**
   * Is this index still the calculation's to overwrite?
   *
   * True when the field is empty, and when it holds the exact value this
   * session's arithmetic put there. Compared as strings because the value
   * arrives as a number from MathUtils.round and as a string from an import or
   * an input.
   */
  indexIsOpenToDerivation(field: string): boolean {
    const current = this.state[field];
    if (current === '' || current === undefined || current === null) {
      return true;
    }
    return String(current) === this.lastDerivedIndex[field];
  }

  /**
   * Store a calculated index, remembering the value so it can be redone.
   *
   * Takes the value already rounded: the rounding is part of the formula and
   * lives in models/BloodworkDerivation, so the lab ingest path and this form
   * cannot round the same marker to different precisions.
   */
  setDerivedIndex(field: string, value: number) {
    this.lastDerivedIndex[field] = String(value);
    this.setState({ [field]: value } as any);
  }

  handleMeanCorpuscularVolumeCalculation(fieldId: string) {
    if (fieldId === 'hematocrit' || fieldId === 'redBloodCells') {
      if (!this.indexIsOpenToDerivation('meanCorpuscularVolume')) return;
      const mcv = deriveMeanCorpuscularVolume(
        this.state.hematocrit,
        this.state.redBloodCells,
      );
      if (mcv !== undefined) {
        this.setDerivedIndex('meanCorpuscularVolume', mcv);
      }
    }
  }

  handleMeanCorpuscularHGBCalculation(fieldId: string) {
    if (fieldId === 'hemoglobin' || fieldId === 'redBloodCells') {
      if (!this.indexIsOpenToDerivation('meanCorpuscularHGB')) return;
      const mch = deriveMeanCorpuscularHGB(
        this.state.hemoglobin,
        this.state.redBloodCells,
      );
      if (mch !== undefined) {
        this.setDerivedIndex('meanCorpuscularHGB', mch);
      }
    }
  }

  handleMCHConcentrationCalculation(fieldId: string) {
    if (fieldId === 'hemoglobin' || fieldId === 'hematocrit') {
      if (!this.indexIsOpenToDerivation('mchConcentration')) return;
      const mchc = deriveMchConcentration(
        this.state.hemoglobin,
        this.state.hematocrit,
      );
      if (mchc !== undefined) {
        this.setDerivedIndex('mchConcentration', mchc);
      }
    }
  }

  handleBilirubinCalculation(fieldId: string) {
    // total = direct + indirect
    // direct = total - indirect
    // indirect = total - direct
    if (
      fieldId === 'bilirubin' ||
      fieldId === 'bilirubinDirect' ||
      fieldId === 'bilirubinIndirect'
    ) {
      const hasTotal = isMeasured(this.state.bilirubin);
      const hasDirect = isMeasured(this.state.bilirubinDirect);
      const hasIndirect = isMeasured(this.state.bilirubinIndirect);
      if (hasTotal && hasDirect && !hasIndirect) {
        const indirect = deriveBilirubinIndirect(
          this.state.bilirubin,
          this.state.bilirubinDirect,
        );
        if (indirect !== undefined) {
          this.setState({ bilirubinIndirect: indirect });
        }
      } else if (hasTotal && hasIndirect && !hasDirect) {
        const direct = deriveBilirubinDirect(
          this.state.bilirubin,
          this.state.bilirubinIndirect,
        );
        if (direct !== undefined) {
          this.setState({ bilirubinDirect: direct });
        }
      }
    }
  }

  handleSaturationCalculation(fieldId: string) {
    if (fieldId === 'iron' || fieldId === 'tibc') {
      /*
       * A report that gives iron and no TIBC is ordinary, not a mistake: the
       * Hospital Joya panel prints Hierro Serico and Transferrina and never a
       * TIBC. % saturation is simply unknown there, and unknown has to stay
       * blank rather than be manufactured. This is the row that surfaced the
       * empty-field trap numericField now covers.
       */
      const saturation = deriveSaturation(this.state.iron, this.state.tibc);
      if (saturation !== undefined) {
        this.setState({ saturation });
      }
    }
  }

  handleTestosteroneBioavailableCalculation(fieldId: string) {
    if (
      fieldId === 'testosteroneBioavailable' ||
      fieldId === 'testosteroneTotal'
    ) {
      const percent = deriveTestosteroneBioavailablePercent(
        this.state.testosteroneBioavailable,
        this.state.testosteroneTotal,
      );
      if (percent !== undefined) {
        this.setState({ testosteroneBioavailablePercent: percent });
      }
    }
  }

  /**
   * eGFR worked out from the rest of the form, or null where it cannot be.
   *
   * The equation itself is in models/BloodworkDerivation (CKD-EPI 2021 for
   * adults, bedside Schwartz under 18). What stays here is where its inputs come
   * from, which is the part no server shares: the age is the client's age AT
   * COLLECTION rather than today, and the height is this test's if it carries
   * one and the client profile's otherwise.
   */
  calculateEGFRFromState(): number | null {
    const client: Client = this.state.selectedClient;
    if (!client?.dateOfBirth || !client?.gender) return null;

    const collectionDate = this.state.collectionDate;
    if (!collectionDate) return null;

    const heightCm =
      this.state.height != null && this.state.height !== ''
        ? Number(this.state.height)
        : client.height;

    return (
      deriveEgfr(this.state.creatinine, {
        ageYears: client.ageAsOf(collectionDate),
        isFemale: client.isFemale(),
        heightCm,
      }) ?? null
    );
  }

  handleLessThanCalculation(fieldId: string) {
    /*
     * eGFR is the one limit that is not a fixed substitution, and it has to be
     * settled FIRST. Both Quest and Labcorp stop reporting it at ">90" (some
     * panels at ">60"), and the real number is recoverable from creatinine, age
     * and sex — so it is calculated from the rest of the form, and the
     * step-over-the-limit number is only the fallback for when the form does not
     * carry enough to calculate it.
     *
     * First, not second, because the general rule now answers every limit rather
     * than only the curated ones. Left in the old order it would set eGFR to
     * 90.1 and this branch would then re-read `this.state` to decide what to do
     * — which only ever saw ">90" because React batches setState inside an event
     * handler. That is a true fact about React 17 and an unreasonable thing for
     * this to depend on.
     */
    if (fieldId === 'eGFR' || fieldId === 'eGFRforAA') {
      const printed = parseAssayLimit(this.state[fieldId]);
      if (printed && printed.direction === '>') {
        const value =
          this.calculateEGFRFromState() ??
          EGFR_FALLBACKS[printed.limit] ??
          fallbackAssayLimitSubstitution(this.state[fieldId]);
        if (value !== undefined) {
          this.setState({ [fieldId]: value });
        }
        return;
      }
    }

    const substitution = assayLimitSubstitution(fieldId, this.state[fieldId]);
    if (substitution !== undefined) {
      this.setState({ [fieldId]: substitution });
    }
  }

  handleCollectionDateChange = (value: any) => {
    const startOfDay = moment(value).startOf('day').toISOString();
    this.setState({
      collectionDate: startOfDay,
      collectionDateForUI: value,
      dirty: true,
    });
    this.clearAlerts();
  };

  handleSubmit = (event: any) => {
    event.preventDefault();

    // Only a NEW test counts against the allowance. Correcting one that is
    // already saved is not adding one, and this used to refuse it: an
    // over-limit practitioner who opened a saved test to fix a typo got the
    // upgrade popup and watched the correction disappear with it.
    if (this.blockedByTestLimit()) {
      this.handleNumberOfTestsLimit();
      return;
    }

    let validationErrors = new Map<string, string>();
    let bloodwork: Bloodwork = new Bloodwork();
    bloodwork.practitionerId = this.props.currentUser.subject;

    for (let key of Object.keys(this.state)) {
      if (
        key !== 'validationErrors' &&
        key !== 'alerts' &&
        key !== 'selectedClient' &&
        key !== 'collectionDateForUI' &&
        key !== 'hideEnterTestResults' &&
        key !== 'panelTitle' &&
        key !== 'disableSelectClient' &&
        key !== 'showUpgradeModal' &&
        key !== 'showCustomMarkersUpgradeModal' &&
        key !== 'showUploadPdfUpgradeModal' &&
        key !== 'showTimeSavingFeaturesModal' &&
        key !== 'showMenstrualPhaseInfo' &&
        key !== 'heightFt' &&
        key !== 'heightIn' &&
        key !== 'bwUnits' &&
        // Custom markers are a nested array, not a scalar. Left to the generic
        // path below they would be mangled: Number([]) is 0, so a test with no
        // custom markers would save `customMarkers: 0`. Assigned explicitly
        // after this loop instead.
        key !== 'customMarkerRows' &&
        key !== 'customMarkerCarriedForwardFrom' &&
        key !== 'customMarkers' &&
        // The same trap a third time: this one arrives in state from the record
        // being edited (every key of it is copied in), and Number([]) is 0, so
        // a test saved with its sections arranged would come back carrying
        // `customMarkerSectionOrder: 0`. Stamped from the cards below instead.
        key !== 'customMarkerSectionOrder' &&
        key !== 'sectionCardOrder' &&
        key !== 'sectionOrderArranged' &&
        key !== 'sectionDragIndex' &&
        key !== 'sectionDropIndex' &&
        // Same trap, and this form has no UI for them: the Recommended
        // Supplements edits are made on the analysis screen. Carried across
        // explicitly below so an edit survives a test being re-saved here.
        key !== 'supplementEdits'
      ) {
        if (this.state[key] === '') {
          // Remove numeric attributes that have empty string
          // DynamoDB will NOT accept empty string....Stupid, right!
          // nulls will be given the value of true....weirdness!
          bloodwork[key] = undefined;
        } else {
          const nbr: number = Number(this.state[key]);
          if (isNaN(nbr)) {
            bloodwork[key] = this.state[key];
          } else {
            bloodwork[key] = nbr;
          }
        }
      }
    }
    // Practitioner-typed, per-test markers. Omitted entirely when there are
    // none, so the attribute never lands on the DynamoDB item as an empty array.
    const customMarkers = customMarkersFromRows(this.state.customMarkerRows);
    bloodwork.customMarkers = customMarkers.length ? customMarkers : undefined;

    // The order the practitioner's own section cards are in, saved only for a
    // test they actually arranged. Left off otherwise, which is what keeps a
    // test following their My Sections list — stamping every save would freeze
    // each test at the order it happened to be saved in, and reordering that
    // list would stop reaching tests already in the book.
    //
    // Read off the CARDS, so what is saved is the order on screen. A name whose
    // rows were all left blank can ride along: those rows are dropped from
    // customMarkers just above, and a ranking entry for a section the test does
    // not have is ignored wherever the order is applied.
    const arrangedSections = this.state.sectionOrderArranged
      ? this.practitionerMarkerSections().map(group => group.label)
      : undefined;
    bloodwork.customMarkerSectionOrder =
      arrangedSections && arrangedSections.length ? arrangedSections : undefined;

    // Carried straight through from the record being edited. Nothing on this
    // form can change them, so the only job here is not to lose them. Omitted
    // when empty, so the attribute never lands on the item as an empty array.
    const supplementEdits = Array.isArray(this.state.supplementEdits)
      ? this.state.supplementEdits
      : undefined;
    bloodwork.supplementEdits =
      supplementEdits && supplementEdits.length ? supplementEdits : undefined;

    // console.log(bloodwork);
    this.validate(bloodwork, validationErrors);
  };

  validate(bloodwork: Bloodwork, validationErrors: Map<string, string>) {
    // console.log(bloodwork);

    // Special check for bilirubin
    if (!bloodwork.isBilirubinValid()) {
      // Use a fake key here so the messages doesn't have the field name in it
      validationErrors.set(
        'fakeBilirubinKey1',
        'Bilirubin, Direct and Bilirubin, Indirect must not be greater than Bilirubin, Total.',
      );
      validationErrors.set(
        'fakeBilirubinKey2',
        'Click the Clear Bilirubin button next to Bilirubin, Total.',
      );
    }

    // Special check for collection date and client DOB
    if (bloodwork.collectionDate && bloodwork.clientId) {
      const { currentClients } = this.props;
      const client = Client.findClient(bloodwork.clientId, currentClients);

      if (!client.isBornOnOrBefore(bloodwork.collectionDate)) {
        validationErrors.set(
          'fakeCollectionDate',
          "Collection Date must be the same or after the client's Date of Birth.",
        );
      }
    }

    // Special check for MPV (Mean Platelet Volume) max value
    if (bloodwork.meanPlateletVolume && bloodwork.meanPlateletVolume > 20) {
      validationErrors.set(
        'meanPlateletVolume',
        'must not be greater than 20 fL.',
      );
    }

    // I³ section scores must be within 0..max for their section.
    for (const s of I3_SECTIONS) {
      const value = (bloodwork as any)[s.id];
      if (value === undefined || value === null || value === '') {
        continue;
      }
      if (value < 0 || value > s.max) {
        validationErrors.set(s.id, `must be between 0 and ${s.max}.`);
      }
    }

    // Practitioner-typed custom markers: name required and unique, lab min/max
    // required and ordered, every entered boundary numeric and non-decreasing.
    validateCustomMarkerRows(this.state.customMarkerRows).forEach(
      (message, key) => {
        validationErrors.set(key, message);
      },
    );

    // Special check to see if menstrualPhase is required and if we should clear it
    if (this.isMenstrualPhaseRequired() && !bloodwork.menstrualPhase) {
      validationErrors.set(
        'menstrualPhase',
        'is required for premenopausal female clients when a sex hormone marker (Estradiol, FSH, LH, Progesterone, etc.) is provided.',
      );
    } else if (!this.showMenstrualPhase()) {
      // If we arent supposed to show it then clear it
      bloodwork.menstrualPhase = undefined;
    }

    // Leptin resolves its reference range by BMI, so a test carrying leptin
    // cannot be graded without a height and a weight. Every leptin range row is
    // BMI-keyed, and findRange returns nothing when none of them matches, so
    // saving without the pair would file a value that can never draw a bar or
    // raise a root cause. Either the client record or this test can supply them,
    // which is the same pair of sources PanelRange.bmiFor() reads.
    if (bloodwork.leptin != null && `${bloodwork.leptin}` !== '') {
      const leptinClient = bloodwork.clientId
        ? Client.findClient(bloodwork.clientId, this.props.currentClients)
        : undefined;
      const heightForLeptin = leptinClient?.height ?? bloodwork.height;
      const weightForLeptin = leptinClient?.weight ?? bloodwork.weight;

      if (!heightForLeptin || !weightForLeptin) {
        validationErrors.set(
          'leptin',
          "needs the client's height and weight, because its reference range is set by BMI. Add them on the client record or on this test.",
        );
      }
    }

    if (validationErrors.size > 0) {
      this.setState({
        validationErrors,
      });

      this.scrollToAddTestResults();
      return;
    }

    validate(bloodwork).then(errors => {
      // errors is an array of validation errors
      if (errors.length > 0) {
        for (let error of errors) {
          this.displayErrorMessage(error, validationErrors);
        }

        this.setState({
          validationErrors: validationErrors,
        });

        this.scrollToAddTestResults();
      } else {
        this.setState({
          validationErrors: new Map<string, string>(),
        });
        // Set WBC Differentials if not entered as percentages
        const wbcDifferentialUnits = parseInt(
          this.state.wbcDifferentialUnits,
          10,
        );
        bloodwork.convertWBCDifferentialsIfNecessary(wbcDifferentialUnits);
        // console.log('bloodwork:', bloodwork);
        this.saveBloodwork(bloodwork);
      }
    });
  }

  saveBloodwork(bloodwork: Bloodwork) {
    const {
      refreshBloodworks,
      loadClientBloodworks,
      bloodworkEntryStore,
      currentClients,
      updateClient,
    } = this.props;

    // Populate the embedded client snapshot for a new test. Clone the live
    // client so stamping height/weight/units onto the snapshot below does not
    // mutate the live record before the change-detection sync block runs (that
    // block compares against the live client to decide whether to persist it).
    if (bloodwork.isNew()) {
      const liveClient = Client.findClient(bloodwork.clientId, currentClients);
      bloodwork.client = liveClient ? new Client(liveClient) : new Client();
    }

    // The height/weight/units entered on a test are the client's measurements
    // as of that test, so the embedded snapshot must carry them. Every BMI /
    // Client-Information readout (analysis screen + PDF) reads bloodwork.client,
    // so without this the display reverts to the client's prior profile value
    // instead of what was just entered. Mirrors saveClientInfo in
    // bloodwork-test-analysis.tsx. Height is cm, weight is lbs (canonical).
    if (bloodwork.client) {
      bloodwork.client.height = bloodwork.height as number | undefined;
      bloodwork.client.weight = bloodwork.weight as number | undefined;
      bloodwork.client.units = this.state.bwUnits === 'SI' ? 'SI' : 'US';
    }

    bloodworkEntryStore
      .saveBloodwork(bloodwork)
      .then(savedBloodwork => {
        // console.log(savedBloodwork);
        this.addAlert(
          { type: 'success', text: 'Successfully saved test result!' },
          false,
          5000,
        );
        // Load the client bloodworks since we could be editing a bloodwork on the analyze bloodworks page
        if (loadClientBloodworks && bloodwork.clientId) {
          loadClientBloodworks(bloodwork.clientId);
        }
        refreshBloodworks();

        // Teach My Markers any custom marker this test introduced, so the next
        // client only needs it picked rather than defined again. Existing
        // library entries are left alone — My Markers is where ranges are
        // curated. Fire-and-forget: a failed profile write must not look like
        // a failed test save, since the test itself is already stored.
        const learned = learnDefinitionsFromMarkers(
          this.props.currentUserProfile?.customMarkerLibrary,
          bloodwork.customMarkers,
          () => uuidv4(),
        );
        if (learned && this.props.learnCustomMarkers) {
          this.props.learnCustomMarkers(learned).catch(err => {
            console.error('Failed to add markers to My Markers:', err);
            // The test is saved either way, so this is a warning rather than an
            // error — but it is not nothing: My Markers has silently stopped
            // learning, and if the cause is a full profile the practitioner
            // needs to hear it from the screen they were on, not discover it
            // the next time a profile save refuses. Kept alongside the save
            // confirmation rather than replacing it.
            this.addAlert(
              {
                type: 'warning',
                text: profileSaveErrorMessage(
                  err,
                  'This test is saved, but its new markers could not be added to My Markers. You can add them there by hand.',
                ),
              },
              true,
            );
          });
        }

        // Sync height/weight/units back to the Client record if they changed
        if (updateClient && bloodwork.clientId) {
          const client = Client.findClient(bloodwork.clientId, currentClients);
          if (client) {
            const newHeight = bloodwork.height as number | undefined;
            const newWeight = bloodwork.weight as number | undefined;
            const newUnits: 'US' | 'SI' = this.state.bwUnits === 'SI' ? 'SI' : 'US';
            // Treat undefined client.units as 'US' (the form default) to avoid spurious saves
            const effectiveClientUnits: 'US' | 'SI' = client.units === 'SI' ? 'SI' : 'US';
            if (client.height !== newHeight || client.weight !== newWeight || effectiveClientUnits !== newUnits) {
              client.height = newHeight;
              client.weight = newWeight;
              client.units = newUnits;
              updateClient(client).catch(err =>
                console.error('Failed to update client height/weight/units:', err),
              );
            }
          }
        }

        this.clearForm(false);
        this.handleNumberOfTestsLimit();
        bloodworkEntryStore.resetCurrentBloodwork();

        // Collapsing the form is the point of the landing: it brings the Test
        // Results panel into view so the next step is right there, in that
        // panel's Actions column (Analyze Results, or the comparison menu) on
        // the row for the test just saved.
        bloodworkEntryStore.setHideEnterTestResults(true);

        // What that panel was analyzing, it was analyzing BEFORE this save, so
        // it describes a different test. Its header reads only "Test Results
        // Analysis" with no client or date on it, so left open beneath a test
        // that was just saved it reads as that test's analysis, one click from
        // an Export to PDF of the wrong client's report.
        bloodworkEntryStore.setAnalyzedBloodwork(undefined, undefined);

        // If we arrived from Manage Test Results, return there with the
        // client preselected so the user lands back where they started.
        const history = this.props.history;
        const navState = history && history.location && history.location.state;
        if (navState && navState.returnTo && history) {
          history.push({
            pathname: navState.returnTo,
            state: { clientId: navState.clientId || bloodwork.clientId },
          });
        }
      })
      .catch(error => {
        console.error(error);
        this.addAlert({ type: 'danger', text: 'Failed to add test result.' });
      });
  }

  clearForm = (clearAlerts?: boolean) => {
    // Nothing is loaded into the form any more, so the next record that arrives
    // has to be, even if it is the one that was just saved.
    this.hydratedBloodworkId = undefined;
    this.clientResolvedBloodworkId = undefined;
    const isAddMode = this.isAddMode(this.props.history);
    const bloodwork = {};
    Object.keys(this.state).forEach(key => {
      if (
        key !== 'validationErrors' &&
        key !== 'collectionDate' &&
        key !== 'alerts' &&
        key !== 'selectedClient' &&
        key !== 'showUpgradeModal' &&
        key !== 'showCustomMarkersUpgradeModal' &&
        key !== 'showUploadPdfUpgradeModal' &&
        key !== 'showTimeSavingFeaturesModal'
      ) {
        bloodwork[key] = '';
      }
    });

    this.setState({
      ...bloodwork,
      panelTitle: this.state.panelTitle,
      validationErrors: new Map<string, string>(),
      collectionDate: undefined,
      collectionDateForUI: undefined,
      // When editing a client's test, keep the client selected — it's the
      // record being edited and the client selector is disabled in edit mode.
      selectedClient: isAddMode ? new Client() : this.state.selectedClient,
      clientId: isAddMode ? '' : this.state.clientId,
      wbcDifferentialUnits: WhiteBloodCellDifferentialUnitType.PERCENT,
      // The blanket `= ''` above would leave the custom-marker table with a
      // string where it expects an array.
      customMarkerRows: [emptyCustomMarkerRow()],
      customMarkerCarriedForwardFrom: undefined,
      sectionCardOrder: undefined,
      sectionOrderArranged: false,
      sectionDragIndex: undefined,
      sectionDropIndex: undefined,
      collapsedCards: {},
      dirty: false,
    },
    // Clear empties the TEST, not the practitioner's setup. The default
    // sections belong to them rather than to any one test, so they come
    // straight back — clearing a mistyped panel otherwise cost them every
    // section they had configured, with no way back short of leaving the
    // screen and returning.
    //
    // In the CALLBACK, not after the call: loadDefaultCustomMarkers reads the
    // rows to decide whether anything has been typed, and setState is async, so
    // called inline it would still see the typed rows and refuse to reload.
    this.loadDefaultCustomMarkers);

    if (clearAlerts) {
      this.clearAlerts();
    }
  };

  clearAlerts = () => {
    this.setState({
      alerts: [],
    });
  };

  displayErrorMessage(
    error: ValidationError,
    validationErrors: Map<string, string>,
  ) {
    for (let constraint of Object.keys(error.constraints)) {
      validationErrors.set(error.property, error.constraints[constraint]);
    }
  }

  renderErrorListItems(validationErrors: Map<string, string>) {
    let listItems: JSX.Element[] = [];
    validationErrors.forEach((value: string, fieldName: string) => {
      if (validationErrors.get(fieldName)) {
        const fieldLabel = this.findFieldLabel(fieldName);
        listItems.push(
          <li key={fieldName + '-error'}>
            {fieldLabel} {validationErrors.get(fieldName)}
          </li>,
        );
      }
    });

    return listItems;
  }

  findFieldLabel(attribute: string): string {
    const panelField: PanelField = PanelField.ALL_FIELDS.find(field => {
      return field.id === attribute;
    });
    return panelField ? panelField.label : '';
  }

  renderValidationErrors(validationErrors: Map<string, string>) {
    return (
      <div className="panel">
        <div className="validation-errors">
          <ul>{this.renderErrorListItems(validationErrors)}</ul>
        </div>
      </div>
    );
  }

  renderAlerts() {
    // console.log('renderAlerts called....');
    const alerts = this.state.alerts.map((alert: any, index: number) => {
      return this.renderAlert(alert, index);
    });

    return <div>{alerts}</div>;
  }

  renderAlert(alert: any, index: number) {
    return (
      <Alert key={index} variant={alert.type} dismissible>
        {alert.text}
      </Alert>
    );
  }

  renderErrors() {
    const { validationErrors } = this.state;
    return validationErrors.size > 0 ? (
      this.renderValidationErrors(validationErrors)
    ) : (
      <div />
    );
  }

  addAlert = (
    alert: any,
    keepCurrentAlerts: boolean = false,
    autoClose?: number,
  ) => {
    // console.log('add alert called....');
    const alerts = keepCurrentAlerts ? [...this.state.alerts, alert] : [alert];
    this.setState({
      alerts,
    });

    if (autoClose) {
      setTimeout(() => {
        this.clearAlerts();
      }, autoClose);
    }
  };

  createFieldElements = (field: PanelField) => {
    if (field.id === 'collectionDate') {
      return this.collectionDate(field);
    }
    if (field.id === 'clientId') {
      return this.selectClient(field);
    }
    if (field.id === 'wbcDifferentialUnits') {
      return this.wbcDifferentialUnits(field);
    }
    if (field.id === 'menstrualPhase') {
      return this.menstrualPhase(field);
    }
    return this.input(field);
  };

  collectionDate(field: PanelField) {
    return (
      // TODO: Look into using a customInput here so that validation styling is applied.
      <DatePicker
        selected={this.state.collectionDateForUI}
        onChange={this.handleCollectionDateChange}
        maxDate={new Date()}
        peekNextMonth
        showMonthDropdown
        showYearDropdown
        todayButton
        dropdownMode="select"
        dateFormat="MM/dd/yyyy"
        placeholderText="MM/DD/YYYY"
        customInput={<Form.Control style={{ fontSize: '14px' }} />}
        popperPlacement="bottom"
        popperModifiers={{
          flip: {
            behavior: ['bottom'], // don't allow it to flip to be above
          },
          preventOverflow: {
            enabled: false, // tell it not to try to stay within the view (this prevents the popper from covering the element you clicked)
          },
          hide: {
            enabled: false, // turn off since needs preventOverflow to be enabled
          },
        }}
      />
    );
  }

  selectClient(field: PanelField) {
    console.log(
      'rendering selectClient......disableSelectClient: ' +
        this.state.disableSelectClient,
    );
    const { currentClients } = this.props;

    if (currentClients === undefined) {
      return <Spinner name="ball-clip-rotate" color="#337ab7" fadeIn="none" />;
    }

    if (currentClients.length === 0) {
      return (
        <LinkContainer exact to="/client/add">
          <Button>Add Client</Button>
        </LinkContainer>
      );
    }

    const sorted = currentClients
      .slice()
      .sort((a: Client, b: Client) =>
        (a.lastName || '').localeCompare(b.lastName || ''),
      );

    const options = sorted.map((c: Client) => ({
      value: c.id,
      label: `${c.lastName}, ${c.firstName}`,
    }));

    const selectedValue =
      options.find((o) => o.value === this.state[field.id]) ?? null;
    const hasError = !!this.state.validationErrors.get(field.id);

    return (
      <Select
        placeholder="Search by name..."
        isSearchable
        // The client cannot be changed on an existing test. Derive this from
        // props (isEditingExistingTest) instead of the disableSelectClient
        // state flag: that flag is only set in componentWillReceiveProps (which
        // can be skipped when editing opens from the Home page Edit button) and
        // is never reset to false, so it would also wrongly stay disabled if an
        // Add form is opened in the same component instance after an edit.
        isDisabled={this.isEditingExistingTest()}
        options={options}
        value={selectedValue}
        onChange={(opt: any) => {
          const rawValue: string = opt?.value ?? '';
          this.setState({ [field.id]: rawValue || undefined });
          this.handleClientChange(rawValue);
          this.clearAlerts();
        }}
        maxMenuHeight={500}
        components={{ Option: ClientOption }}
        styles={{
          /*
           * Capped, because this field was sized by the layout rather than by
           * what goes in it.
           *
           * Its column is one of three equal thirds of the form, so the Select
           * filled 443px at a 1440px window and 563px at 1800px, growing with
           * the window forever. A client name is "Lastname, Firstname":
           * eleven characters for "Smith, John", twenty-two for "Rodriguez,
           * Christopher", and the longest plausible one measures 216px.
           *
           * Capped to the Browse row below it rather than to the names, so
           * the two rows end on the same vertical line. That is wider than
           * the names strictly need, and the alignment is worth the
           * difference: a right edge shared with the row underneath reads as
           * deliberate where a field ending on its own reads as an accident.
           * It starts at the same x as that row because sm={4} on the upload
           * label is the same third as col-sm here.
           */
          container: (base: any) => ({
            ...base,
            maxWidth: `${CLIENT_SELECT_MAX_WIDTH}px`,
          }),
          control: (base: any, state: any) => ({
            ...base,
            borderColor: hasError
              ? '#dc3545'
              : state.isFocused
              ? '#80bdff'
              : '#ced4da',
            boxShadow: hasError
              ? '0 0 0 0.2rem rgba(220,53,69,.25)'
              : state.isFocused
              ? '0 0 0 0.2rem rgba(0,123,255,.25)'
              : 'none',
            '&:hover': { borderColor: hasError ? '#dc3545' : '#ced4da' },
            fontSize: '14px',
            minHeight: '38px',
          }),
          /*
           * Wider than the field it hangs from, when the names need it.
           *
           * The cap above is on the control, which displays ONE name that the
           * practitioner already chose. The menu is where they read the names
           * to choose between, and a list that truncated them would make the
           * narrower field cost something real. max-content sizes it to the
           * longest name, min-width keeps it at least as wide as the control,
           * and the max stops one freak entry throwing a 900px panel across
           * the form.
           */
          menu: (base: any) => ({
            ...base,
            zIndex: 9999,
            width: 'max-content',
            minWidth: '100%',
            maxWidth: '420px',
          }),
          placeholder: (base: any) => ({
            ...base,
            color: '#6c757d',
            fontSize: '14px',
          }),
          singleValue: (base: any) => ({
            ...base,
            fontSize: '12px',
            fontWeight: 600,
          }),
        }}
      />
    );
  }

  wbcDifferentialUnits(field: PanelField) {
    let wbcDifferentialUnits = parseInt(this.state.wbcDifferentialUnits, 10);
    // Make sure we default a value if none present
    if (!wbcDifferentialUnits) {
      wbcDifferentialUnits = WhiteBloodCellDifferentialUnitType.ABSOLUTE_KCELLS;
    }
    return (
      <InputGroup>
        <FormCheck
          inline
          id="unit2"
          name={field.id}
          value={WhiteBloodCellDifferentialUnitType.ABSOLUTE_KCELLS}
          label={WhiteBloodCellDifferentialUnit.ABSOLUTE_KCELLS}
          type="radio"
          onChange={this.handleInputChange}
          checked={
            wbcDifferentialUnits ===
            WhiteBloodCellDifferentialUnitType.ABSOLUTE_KCELLS
          }
        ></FormCheck>
        &nbsp;&nbsp;&nbsp;
        <FormCheck
          inline
          id="unit3"
          name={field.id}
          value={WhiteBloodCellDifferentialUnitType.ABSOLUTE_CELLS}
          label={WhiteBloodCellDifferentialUnit.ABSOLUTE_CELLS}
          type="radio"
          onChange={this.handleInputChange}
          checked={
            wbcDifferentialUnits ===
            WhiteBloodCellDifferentialUnitType.ABSOLUTE_CELLS
          }
        ></FormCheck>
        &nbsp;&nbsp;&nbsp;
        <FormCheck
          id="unit1"
          name={field.id}
          value={WhiteBloodCellDifferentialUnitType.PERCENT}
          inline
          label={WhiteBloodCellDifferentialUnit.PERCENT}
          type="radio"
          onChange={this.handleInputChange}
          checked={
            wbcDifferentialUnits === WhiteBloodCellDifferentialUnitType.PERCENT
          }
        ></FormCheck>
      </InputGroup>
    );
  }

  menstrualPhase(field: PanelField) {
    return (
      <Form.Control
        as="select"
        name="menstrualPhase"
        value={this.state.menstrualPhase}
        onChange={this.handleInputChange}
        isInvalid={!!this.state.validationErrors.get(field.id)}
      >
        <option value="">{field.placeholder}</option>
        <option value={MenstrualPhase.FOLLICULAR}>
          {MenstrualPhase.FOLLICULAR}
        </option>
        <option value={MenstrualPhase.OVULATION}>
          {MenstrualPhase.OVULATION}
        </option>
        <option value={MenstrualPhase.LUTEAL}>{MenstrualPhase.LUTEAL}</option>
        <option value={MenstrualPhase.NONE}>
          {MenstrualPhase.NONE} (children's ranges)
        </option>
      </Form.Control>
    );
  }

  input(field: PanelField) {
    const { validationErrors } = this.state;

    return (
      <InputGroup>
        <Form.Control
          name={field.id}
          onChange={this.handleInputChange}
          onBlur={this.handleOnBlur}
          placeholder={this.placeholder(field)}
          type="text"
          value={this.state[field.id]}
          isInvalid={!!validationErrors.get(field.id)}
        />
        {this.units(field)}
      </InputGroup>
    );
  }

  units(field: PanelField) {
    let units = field.units;
    const wbcDifferentialUnits = parseInt(this.state.wbcDifferentialUnits, 10);
    if (field.isWBCDifferential) {
      units = field.wbcUnits(wbcDifferentialUnits);
    }
    return units ? (
      <InputGroup.Append>
        <InputGroup.Text>{units}</InputGroup.Text>
      </InputGroup.Append>
    ) : (
      ''
    );
  }

  placeholder(field: PanelField): string {
    if (field.isWBCDifferential) {
      const wbcDifferentialUnits = parseInt(
        this.state.wbcDifferentialUnits,
        10,
      );
      return field.wbcPlaceholder(wbcDifferentialUnits);
    } else if (field.placeholder) {
      return field.placeholder;
    } else {
      const { bloodworkRanges } = this.props;
      const { selectedClient, collectionDate, menstrualPhase } = this.state;
      const bloodwork = new Bloodwork({
        client: selectedClient,
        collectionDate: collectionDate || moment().startOf('day').toISOString(),
        menstrualPhase,
      });

      const range = BloodworkRange.findRange(
        bloodworkRanges,
        field.id,
        bloodwork,
      );

      if (range) {
        // console.log('placeholder.................................');
        // console.log(range);
        return range.placeholder(field.getPlaceholderPrecision());
      }
    }
    // No range for this client, so no example to derive from one. A marker that
    // expects to land here supplies its own (PanelField.fallbackPlaceholder);
    // every other marker keeps the blank box it has always had.
    return field.fallbackPlaceholder;
  }

  clearBilirubin() {
    this.setState({
      bilirubin: '',
      bilirubinDirect: '',
      bilirubinIndirect: '',
    });
  }

  addAdditionalInfo(field: PanelField) {
    /*
     * Why the number on the form may not be the number on the report.
     *
     * LabSmarts charts Lp(a) in nmol/L and the labs are split: Labcorp, Quest,
     * CPL, East Side, Ulta and Function print nmol/L, while Vibrant and Access
     * print mg/dL. An import multiplies the mass reports by 2.8, so a Vibrant
     * report reading 15 lands here as 42 — and a practitioner checking the
     * form against the page they are holding sees a number that is nowhere on
     * it. Said in the column rather than only on hover for that reason: the
     * moment it is needed is the moment somebody is comparing two numbers and
     * deciding one of them is wrong.
     *
     * The tooltip carries the part that does not fit, including that a
     * converted value is an estimate. See lpaMassToMolar in the uploadPdf
     * Lambda's utils.js.
     */
    if (field.id === 'lipoproteinA') {
      const tooltip = (
        <Tooltip id="lipoprotein-a-tooltip">
          <div>
            LabSmarts charts Lp(a) in nmol/L. Labcorp, Quest, CPL, East Side,
            Ulta and Function report nmol/L and are entered exactly as printed.
          </div>
          <div style={{ marginTop: '6px' }}>
            Vibrant and Access report Lp(a) in mg/dL. An upload multiplies those
            by 2.8, so a Vibrant report reading 15 mg/dL is entered as 42.
          </div>
          <div style={{ marginTop: '6px' }}>
            A converted result is an estimate. Mass and particle count are
            different quantities and the ratio varies between people, so enter
            a nmol/L result directly whenever the lab reports one.
          </div>
        </Tooltip>
      );
      return (
        <OverlayTrigger overlay={tooltip}>
          {/*
           * A full-height flex box rather than inline text, so the note sits
           * on the input's centre line instead of its top edge. The column is
           * stretched to the row's height by Bootstrap; without the height the
           * span is only as tall as its own line and starts at the top, which
           * leaves the note riding above the field it belongs to. Centring the
           * icon against the words is the same flex doing double duty.
           */}
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              height: '100%',
              fontSize: '0.8125rem',
              color: '#6c757d',
              cursor: 'pointer',
            }}
          >
            <FontAwesomeIcon
              icon="info-circle"
              style={{ marginRight: '5px', color: '#337ab7' }}
            />
            mg/dL reports are converted
          </span>
        </OverlayTrigger>
      );
    }

    if (field.id === 'bilirubin' || field.id === 'bilirubinDirect') {
      const tooltip = (
        <Tooltip id="bilirubin-tooltip">
          <div>
            Click this button to clear the values for Bilirubin, Total, Direct,
            and Indirect if a mistake was made in entering Bilirubin, Direct or
            Indirect.
          </div>
          <div>
            Re-enter the value for Bilirubin, Total first, then enter a value
            for either Direct or Indirect and the other will be automatically
            calculated.
          </div>
        </Tooltip>
      );
      return (
        <OverlayTrigger overlay={tooltip}>
          <Button
            type="button"
            onClick={() => {
              this.clearBilirubin();
            }}
          >
            Clear Bilirubin
          </Button>
        </OverlayTrigger>
      );
    }
    return null;
  }

  isMenstrualPhaseRequired() {
    // Any marker whose ranges depend on the menstrual phase requires one to be
    // selected — not just Estradiol. Check every phase-dependent marker so
    // saving FSH/LH/Progesterone/etc. without a phase is also blocked.
    const hasPhaseDependentMarker = PanelRange.MENSTRUAL_PHASE_ATTRIBUTES.some(
      attribute => {
        const value = this.state[attribute];
        return value !== undefined && value !== null && value !== '';
      },
    );
    return hasPhaseDependentMarker && this.showMenstrualPhase();
  }

  showMenstrualPhase() {
    const { selectedClient, collectionDate } = this.state;
    if (!selectedClient || !selectedClient.isPremenopausal()) {
      return false;
    }
    // The collection date is only needed for the age check, so the field does
    // not wait on one. Until a date is picked, fall back to today, the same
    // substitution the range placeholder lookup makes.
    const age = selectedClient.ageAsOf(
      collectionDate || moment().startOf('day').toISOString(),
    );
    return age > 10;
  }

  renderMenstrualPhaseInfoIcon() {
    const tooltip = (
      <Tooltip id="menstrual-phase-info-tooltip">
        Click to view the phases of the menstrual cycle.
      </Tooltip>
    );
    return (
      <OverlayTrigger overlay={tooltip}>
        <FontAwesomeIcon
          icon="info-circle"
          style={{ marginLeft: '6px', cursor: 'pointer', color: '#337ab7' }}
          onClick={() => this.setState({ showMenstrualPhaseInfo: true })}
        />
      </OverlayTrigger>
    );
  }

  formGroup(field: PanelField) {
    const className = field.isFormCheck ? 'labsmarts-form-check-label' : '';
    // Only show menstrual phase if necessary
    if (field.id === 'menstrualPhase' && !this.showMenstrualPhase()) {
      return null;
    }
    return (
      <Form.Group
        as={Row}
        key={field.id + '-group'}
        controlId={field.id + '-group'}
      >
        <Form.Label column sm className={className}>
          {field.label}
          {field.id === 'menstrualPhase' && this.renderMenstrualPhaseInfoIcon()}
        </Form.Label>
        <Col sm>{this.createFieldElements(field)}</Col>
        <Col sm>{this.addAdditionalInfo(field)}</Col>
      </Form.Group>
    );
  }

  // A yes/no toggle for the I³ section, persisted as a 0/1 number so it flows
  // through the same save pipeline as the numeric fields.
  i3ToggleGroup(id: string, label: string) {
    const checked = !!Number(this.state[id]);
    return (
      <Form.Group as={Row} key={id + '-group'} controlId={id + '-group'}>
        <Form.Label column sm>
          {label}
        </Form.Label>
        <Col sm>
          <Form.Check
            type="checkbox"
            id={id}
            name={id}
            label="Yes"
            checked={checked}
            onChange={(e: any) =>
              this.setState({ [id]: e.target.checked ? 1 : 0 })
            }
          />
        </Col>
        <Col sm />
      </Form.Group>
    );
  }

  // Add Test Results card for the Immune & Inflammatory Burden Index (I³).
  // Env-gated to local dev only (hidden in slava + prod), matching the report sections.
  renderImmuneInflammatoryCard() {
    return (
      <Card>
        <Card.Header>Immune &amp; Inflammatory Burden Index (I³)</Card.Header>
        <Card.Body>
          <p style={{ fontSize: '13px', color: '#6c757d', marginBottom: '12px' }}>
            Enter each section's questionnaire subtotal (0 to the shown maximum).
            Weighted scores, the total Weighted I³ Score, and the Root Cause
            triggers are calculated automatically.
          </p>
          <Form noValidate>
            {PanelField.IMMUNE_INFLAMMATORY_FIELDS.map((field: any) =>
              field.isNotEditable ? null : this.formGroup(field),
            )}
            <hr />
            <p style={{ fontSize: '13px', color: '#6c757d', marginBottom: '12px' }}>
              Toxins and Trauma share the same questionnaire sections. These
              optional flags help separate them.
            </p>
            {this.i3ToggleGroup('i3ToxinExposure', 'Known toxin / mold exposure')}
            {this.i3ToggleGroup('i3TraumaHistory', 'Trauma / ACE / chronic-stress history')}
          </Form>
        </Card.Body>
      </Card>
    );
  }

  // Add Test Results card for Metabolic Vulnerability (MVX Plus). Gated by
  // mvxVisible() (local dev + slava, hidden in prod), matching the report
  // section.
  renderMvxCard() {
    return (
      <Card>
        <Card.Header>
          Metabolic Vulnerability Index (MVX) Plus
          <OverlayTrigger
            overlay={<Tooltip id="mvx-labcorp-tooltip">View the Labcorp MVX Plus test page (#123601)</Tooltip>}
          >
            <a
              href={LABCORP_MVX_PLUS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="external-link"
              style={{ marginLeft: '8px' }}
            >
              <FontAwesomeIcon icon="external-link-alt" />
            </a>
          </OverlayTrigger>
        </Card.Header>
        <Card.Body>
          {/* No instruction line. This is the Add Test Results screen: the
              card's name and the fields say what goes in it, the way every
              other panel card on this page does (Maryann, 2026-10-10). */}
          <Form noValidate>
            {PanelField.MVX_FIELDS.map((field: any) =>
              field.isNotEditable ? null : this.formGroup(field),
            )}
          </Form>
        </Card.Body>
      </Card>
    );
  }

  scrollToAddTestResults() {
    const element = document.getElementById('add-test-results-panel');
    if (element) {
      element.scrollIntoView();
    }
  }

  showTimeSavingFeaturesModal = () => {
    this.setState({
      showTimeSavingFeaturesModal: true,
    });
  };

  async displayTimeSavingFeaturesModal(): Promise<boolean> {
    const { currentUserProfile } = this.props;
    if (currentUserProfile) {
      const hasSeenTimeSavingFeaturesModal = await localStorage.getItem(
        `${currentUserProfile.id}-hasSeenTimeSavingFeaturesModal`,
      );
      return hasSeenTimeSavingFeaturesModal !== 'true';
    } else {
      return Promise.resolve(false);
    }
  }

  hasSeenTimeSavingFeaturesModal = (): void => {
    // console.log('hasSeenTimeSavingFeaturesModal invoked....');
    const { currentUserProfile } = this.props;
    localStorage.setItem(
      `${currentUserProfile.id}-hasSeenTimeSavingFeaturesModal`,
      'true',
    );
    this.setState({
      showTimeSavingFeaturesModal: false,
    });
  };

  scrollTop() {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }

  // Lands the top of the Add Test Results panel just below the sticky header.
  // React Router (v5) preserves the previous page's scroll offset across a push
  // navigation, so arriving here from a long list (e.g. the Manage Clients
  // table, scrolled well down) inherits that offset and leaves the user partway
  // into the form, around the BMI row above CBC. A single synchronous reset on
  // mount doesn't stick because the browser re-applies the inherited offset and
  // async data finishes settling the layout afterward, so we re-run on the next
  // paint and once more after a short delay. Mirrors scrollToAnalyzeTestResults.
  scrollToAddTestResultsTop() {
    const run = () => {
      const element = document.getElementById('add-test-results-panel');
      if (!element) {
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
        return;
      }
      const sticky = document.querySelector('.App-sticky-top') as HTMLElement | null;
      const stickyHeight = sticky ? sticky.getBoundingClientRect().height : 0;
      const top = element.getBoundingClientRect().top + window.scrollY - stickyHeight;
      window.scrollTo({ top: Math.max(top, 0) });
    };
    run();
    requestAnimationFrame(run);
    setTimeout(run, 300);
  }

  toggleEnterTestResults() {
    const hideEnterTestResults = !this.state.hideEnterTestResults;
    // Local state for the immediate repaint, the store because the store is
    // what owns this. componentWillReceiveProps re-mirrors the store value on
    // every incidental re-render (the container observes the profile, the
    // ranges, the client list and the further considerations, and re-renders as
    // each one lands), so a collapse written only to local state was undone by
    // whichever of those arrived next. That went unnoticed while a save left the
    // store saying "collapsed" anyway; now that a save leaves the panel open,
    // the chevron is the only thing that closes it and it has to stick.
    this.setState({ hideEnterTestResults });
    this.props.bloodworkEntryStore?.setHideEnterTestResults(
      hideEnterTestResults,
    );
  }

  render() {
    const {
      panelTitle,
      showUpgradeModal,
      showUploadPdfUpgradeModal,
      showTimeSavingFeaturesModal,
      hideEnterTestResults,
    } = this.state;
    const addTestResultsInfoTooltip = (
      <Tooltip id="modal-tooltip">
        Click this icon to learn more about time saving features.
      </Tooltip>
    );
    const hideEnterTestResultsIconName = hideEnterTestResults
      ? 'chevron-down'
      : 'chevron-up';

    return (
      <div>
        {/* In-app navigation. The menu, the client list and the Home page are
            all one click from here, and nothing on this form is autosaved. */}
        <Prompt
          when={this.state.dirty}
          message="This test has not been saved yet. Leave the page and the results you have entered will be lost."
        />
        {this.renderAlerts()}

        {/* The limit, stated where it cannot be missed and cannot be dismissed.
            A modal on mount was the only warning, and it is dismissible: the
            practitioner closed it, entered a whole panel including every custom
            marker, pressed Save, and got the same modal back with nothing
            saved. An hour of typing for a refusal that was knowable before the
            first field.

            Not an alert they can close, for the same reason: the answer to "can
            I save this" must be on screen the entire time it is being filled
            in. The Save buttons below are disabled to match, so the refusal is
            never a surprise sprung at the end. */}
        {this.blockedByTestLimit() ? (
          <Alert variant="warning">
            <strong>
              You have reached the maximum number of test results allowed.
            </strong>{' '}
            A new test cannot be saved until your plan is upgraded, so please
            upgrade before entering one. You can still open and edit the tests
            you have already saved.
            <div className="mt-2">
              <a
                href="https://labsmarts.com/pricing"
                target="_blank"
                rel="noopener noreferrer"
              >
                Free user - click here to upgrade.
              </a>
              <br />
              <a
                href="https://labsmarts.com/my-account"
                target="_blank"
                rel="noopener noreferrer"
              >
                Paid user - click here to upgrade
              </a>
            </div>
          </Alert>
        ) : null}

        <GenericModal
          show={showUpgradeModal}
          title="Maximum Number of Test Results Reached"
          text="You have reached the maximum number of test results allowed."
          link="https://labsmarts.com/pricing"
          linkText="Free user - click here to upgrade."
          additionalLink="https://labsmarts.com/my-account"
          additionalLinkText="Paid user - click here to upgrade"
          onHide={() => this.setState({ showUpgradeModal: false })}
        />

        <GenericModal
          show={this.state.showCustomMarkersUpgradeModal}
          title={CUSTOM_MARKER_UPGRADE_MODAL.title}
          text={CUSTOM_MARKER_UPGRADE_MODAL.text}
          link={CUSTOM_MARKER_UPGRADE_MODAL.link}
          linkText={CUSTOM_MARKER_UPGRADE_MODAL.linkText}
          additionalLink={CUSTOM_MARKER_UPGRADE_MODAL.additionalLink}
          additionalLinkText={CUSTOM_MARKER_UPGRADE_MODAL.additionalLinkText}
          onHide={() =>
            this.setState({ showCustomMarkersUpgradeModal: false })
          }
        />

        <GenericModal
          show={showUploadPdfUpgradeModal}
          title="Upload Test Results Feature Not Available"
          text="Upload Test Results feature is not available."
          link="https://labsmarts.com/upload_test_results_add_on"
          linkText="Click here to upgrade."
          onHide={() => this.setState({ showUploadPdfUpgradeModal: false })}
        />

        <TimeSavingFeaturesModal
          show={showTimeSavingFeaturesModal}
          onHide={this.hasSeenTimeSavingFeaturesModal}
        />

        <Modal
          show={this.state.showMenstrualPhaseInfo}
          onHide={() => this.setState({ showMenstrualPhaseInfo: false })}
          size="lg"
          centered
        >
          <Modal.Header
            closeButton
            style={{ backgroundColor: '#337ab7', color: '#fff' }}
          >
            <Modal.Title>Menstrual Cycle Phases</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center">
            <img
              src={menstrualCycleDiagram}
              alt="Diagram of the female menstrual cycle showing the follicular and luteal phases, ovulation, and menstruation"
              style={{ maxWidth: '100%', height: 'auto' }}
            />
          </Modal.Body>
        </Modal>

        <Card id="add-test-results-panel">
          <Card.Header onClick={() => this.toggleEnterTestResults()}>
            <FontAwesomeIcon icon={hideEnterTestResultsIconName} />
            &nbsp;&nbsp;{panelTitle}
            <OverlayTrigger overlay={addTestResultsInfoTooltip}>
              <FontAwesomeIcon
                icon="info-circle"
                className="float-right"
                onClick={this.showTimeSavingFeaturesModal}
              />
            </OverlayTrigger>
          </Card.Header>

          {hideEnterTestResults ? null : (
            <Card.Body>
              {this.renderErrors()}
              <br />
              <Form noValidate>
                {PanelField.GENERAL_FIELDS.map((field: any, index: number) => {
                  return field.isNotEditable ? null : this.formGroup(field);
                })}
                {this.props.currentUserProfile?.canSeeUploadTestResults() && (
                  <Upload
                    isUploadAvailable={this.props.currentUserProfile?.canUploadPdf()}
                    showModal={() => this.handleUploadPdfLimit()}
                    setState={obj => {
                      this.setState(prev => ({ ...prev, ...obj }));
                      setTimeout(() =>
                        Object.keys(obj).forEach(key =>
                          this.handleOnBlur({ target: { name: key } }),
                        ),
                      );
                    }}
                    clearForm={() => this.clearForm(true)}
                    /*
                     * The rows the free parse could not give to a shipped
                     * marker, offered to this practitioner's My Markers.
                     *
                     * Reads this.state rather than a setState updater because
                     * it has to RETURN what it could not place: the upload puts
                     * that on screen. Safe here — the only other thing that
                     * upload sets is the shipped fields, which are not rows.
                     */
                    onExtraMarkers={(extras: ImportedLabRow[]) => {
                      const profile = this.props.currentUserProfile;
                      // An entitlement that has been turned off does not blank
                      // the markers already on a test, and it must not quietly
                      // add new ones either.
                      if (!profile?.canUseCustomMarkers()) {
                        // Typed rather than asserted: `as` let this go on
                        // returning an outcome with no `filled` after the field
                        // was added, which the upload reads without checking.
                        const none: ImportedRowsOutcome = {
                          rows: [],
                          filled: [],
                          unmatched: [],
                          mismatched: [],
                        };
                        return none;
                      }
                      const placed = fillRowsFromImport(
                        this.state.customMarkerRows || [],
                        extras,
                        profile.customMarkerLibrary,
                        this.state.selectedClient?.gender,
                      );
                      if (placed.rows !== this.state.customMarkerRows) {
                        this.setState({ customMarkerRows: placed.rows });
                      }
                      return placed;
                    }}
                    onReviewImport={result => {
                      // Values land like any other upload; the custom markers
                      // the read proposed are APPENDED to whatever rows the
                      // practitioner already has, never replacing them.
                      // The collection date is FREE TEXT on the review screen
                      // — the practitioner corrects what the read guessed —
                      // and moment() accepts anything. A date it cannot parse
                      // used to be stored as `null` with an "Invalid Date" in
                      // the picker beside it, which is a worse outcome than
                      // leaving the field alone for them to fill in.
                      const readDate = result.collectionDate
                        ? moment(result.collectionDate)
                        : undefined;
                      const dateFields =
                        readDate && readDate.isValid()
                          ? {
                              collectionDate: readDate
                                .clone()
                                .startOf('day')
                                .toISOString(),
                              collectionDateForUI: readDate.toDate(),
                            }
                          : {};
                      this.setState(
                        prev => {
                          // A second "Scan for more markers" over the same
                          // report proposes the same markers again. Appended
                          // blind they become duplicate rows, which the save
                          // validator then refuses ("this name is already used
                          // on row N") — so the practitioner is blocked by a
                          // button that looks like it worked.
                          const kept = (prev.customMarkerRows || []).filter(
                            row => !isBlankCustomMarkerRow(row),
                          );
                          const alreadyOnTest = rowKeysInUse(kept);
                          const added = rowsFromCustomMarkers(
                            result.customMarkers,
                          ).filter(row => {
                            const key = CustomMarker.keyFor(row.name);
                            if (!row.name.trim() || alreadyOnTest.has(key)) {
                              return false;
                            }
                            // Tracked as we go, so one read proposing the same
                            // name twice cannot add it twice either.
                            alreadyOnTest.add(key);
                            return true;
                          });
                          return {
                          ...prev,
                          ...result.values,
                          ...dateFields,
                          customMarkerRows: [
                            ...kept,
                            ...added,
                            emptyCustomMarkerRow(),
                          ],
                          };
                        },
                        () =>
                          Object.keys(result.values).forEach(key =>
                            this.handleOnBlur({ target: { name: key } }),
                          ),
                      );
                    }}
                  />
                )}
                <Form.Group as={Row} controlId="bwUnitsGroup">
                  <Form.Label column sm={4} className="labsmarts-form-check-label">Units</Form.Label>
                  <Col sm={5}>
                    <Form.Check
                      className="form-check-inline-first"
                      inline
                      id="bw-units-us"
                      name="bwUnits"
                      label="US"
                      type="radio"
                      checked={this.state.bwUnits !== 'SI'}
                      onChange={() => {
                        const cm = this.state.height;
                        const { ft, inches } = cm != null ? MathUtils.cmToFtIn(Number(cm)) : { ft: '', inches: '' };
                        this.setState({
                          bwUnits: 'US',
                          heightFt: cm != null ? String(ft) : '',
                          heightIn: cm != null ? String(inches) : '',
                        });
                      }}
                    />
                    <Form.Check
                      inline
                      id="bw-units-si"
                      name="bwUnits"
                      label="SI"
                      type="radio"
                      checked={this.state.bwUnits === 'SI'}
                      onChange={() => {
                        const ft = Number(this.state.heightFt) || 0;
                        const inches = Number(this.state.heightIn) || 0;
                        const cm = (this.state.heightFt || this.state.heightIn)
                          ? MathUtils.ftInToCm(ft, inches)
                          : this.state.height;
                        this.setState({ bwUnits: 'SI', height: cm });
                      }}
                    />
                    &nbsp;
                    <OverlayTrigger
                      placement="right"
                      overlay={
                        <Tooltip id="bw-units-tooltip">
                          Currently only applies to height and weight.
                          <br /><br />
                          SI units for markers will be available in a future update.
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon
                        className="external-link"
                        icon="info-circle"
                        style={{ verticalAlign: 'middle', marginTop: '-2px', paddingBottom: '1px' }}
                      />
                    </OverlayTrigger>
                  </Col>
                </Form.Group>
                <Form.Group as={Row} controlId="heightEntryGroup">
                  <Form.Label column sm={4}>Height</Form.Label>
                  <Col sm={this.state.bwUnits === 'SI' ? 3 : 5}>
                    {this.state.bwUnits !== 'SI' ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <div style={{ width: '78px' }}>
                          <Select
                            isSearchable={false}
                            maxMenuHeight={400}
                            onChange={(opt: any) => {
                              const ft = opt?.value ?? '';
                              const inches = Number(this.state.heightIn) || 0;
                              const cm = ft !== ''
                                ? MathUtils.ftInToCm(Number(ft), inches)
                                : (this.state.heightIn === '' ? undefined : this.state.height);
                              this.setState({ heightFt: ft, height: cm });
                            }}
                            options={[
                              { value: '', label: '—' },
                              ...[1,2,3,4,5,6,7,8].map(n => ({ value: String(n), label: String(n) })),
                            ]}
                            styles={{
                              control: (b: any) => ({ ...b, minHeight: '31px', height: '31px', borderColor: '#ced4da', boxShadow: 'none', fontSize: '12px' }),
                              valueContainer: (b: any) => ({ ...b, padding: '0 6px', height: '31px' }),
                              input: (b: any) => ({ ...b, margin: '0', padding: '0' }),
                              indicatorsContainer: (b: any) => ({ ...b, height: '31px' }),
                              dropdownIndicator: (b: any) => ({ ...b, padding: '4px' }),
                              menu: (b: any) => ({ ...b, zIndex: 9999 }),
                              option: (b: any) => ({ ...b, fontSize: '12px', padding: '4px 8px' }),
                              singleValue: (b: any) => ({ ...b, fontSize: '12px' }),
                            }}
                            value={{ value: this.state.heightFt ?? '', label: this.state.heightFt || '—' }}
                          />
                        </div>
                        <span className="input-group-text">ft</span>
                        <div style={{ width: '78px' }}>
                          <Select
                            isSearchable={false}
                            maxMenuHeight={400}
                            onChange={(opt: any) => {
                              const inches = opt?.value ?? '';
                              const ft = Number(this.state.heightFt) || 0;
                              const cm = inches !== ''
                                ? MathUtils.ftInToCm(ft, Number(inches))
                                : (this.state.heightFt === '' ? undefined : this.state.height);
                              this.setState({ heightIn: inches, height: cm });
                            }}
                            options={[
                              { value: '', label: '—' },
                              ...[0,1,2,3,4,5,6,7,8,9,10,11].map(n => ({ value: String(n), label: String(n) })),
                            ]}
                            styles={{
                              control: (b: any) => ({ ...b, minHeight: '31px', height: '31px', borderColor: '#ced4da', boxShadow: 'none', fontSize: '12px' }),
                              valueContainer: (b: any) => ({ ...b, padding: '0 6px', height: '31px' }),
                              input: (b: any) => ({ ...b, margin: '0', padding: '0' }),
                              indicatorsContainer: (b: any) => ({ ...b, height: '31px' }),
                              dropdownIndicator: (b: any) => ({ ...b, padding: '4px' }),
                              menu: (b: any) => ({ ...b, zIndex: 9999 }),
                              option: (b: any) => ({ ...b, fontSize: '12px', padding: '4px 8px' }),
                              singleValue: (b: any) => ({ ...b, fontSize: '12px' }),
                            }}
                            value={{ value: this.state.heightIn ?? '', label: this.state.heightIn !== '' && this.state.heightIn != null ? String(this.state.heightIn) : '—' }}
                          />
                        </div>
                        <span className="input-group-text">in</span>
                      </div>
                    ) : (
                      <InputGroup>
                        <Form.Control
                          maxLength={3}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const cm = e.target.value;
                            this.setState({ height: cm === '' ? undefined : Number(cm) });
                          }}
                          placeholder="e.g. 170"
                          size="sm"
                          style={{ maxWidth: '78px' }}
                          type="text"
                          value={this.state.height != null ? this.state.height : ''}
                        />
                        <InputGroup.Append>
                          <InputGroup.Text>cm</InputGroup.Text>
                        </InputGroup.Append>
                      </InputGroup>
                    )}
                  </Col>
                </Form.Group>
                <Form.Group as={Row} controlId="weightEntryGroup">
                  <Form.Label column sm={4}>Weight</Form.Label>
                  <Col sm={3}>
                    <InputGroup>
                      <Form.Control
                        size="sm"
                        maxLength={3}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const val = e.target.value;
                          if (val.trim() !== '' && Number(val) > 999) return;
                          if (this.state.bwUnits === 'SI') {
                            this.setState({ weight: val.trim() === '' ? '' : MathUtils.kgToLbs(Number(val)) });
                          } else {
                            this.setState({ weight: val });
                          }
                        }}
                        placeholder={this.state.bwUnits === 'SI' ? 'e.g. 68' : 'e.g. 150'}
                        style={{ maxWidth: '78px' }}
                        type="text"
                        value={
                          this.state.bwUnits === 'SI' && this.state.weight != null && this.state.weight !== ''
                            ? MathUtils.lbsToKg(Number(this.state.weight))
                            : (this.state.weight != null ? this.state.weight : '')
                        }
                      />
                      <InputGroup.Append>
                        <InputGroup.Text>{this.state.bwUnits === 'SI' ? 'kg' : 'lbs'}</InputGroup.Text>
                      </InputGroup.Append>
                    </InputGroup>
                  </Col>
                </Form.Group>
                {(() => {
                  const heightCm = this.state.height != null && this.state.height !== '' ? Number(this.state.height) : null;
                  const weightLbs = this.state.weight != null && this.state.weight !== '' ? Number(this.state.weight) : null;
                  if (heightCm == null || weightLbs == null) return null;
                  const bmi = MathUtils.bmi(weightLbs, heightCm);
                  return (
                    <Form.Group as={Row} controlId="bmiDisplayGroup">
                      <Form.Label column sm={4}>BMI</Form.Label>
                      <Col sm={3}>
                        {/* Calculated from height and weight, so it carries the
                            same gray fill Bootstrap gives a disabled control. */}
                        <div
                          title="Calculated from height and weight"
                          style={{
                            border: '1px solid #ced4da',
                            borderRadius: '0.25rem',
                            padding: '0.25rem 0.5rem',
                            fontSize: '14px',
                            display: 'inline-block',
                            minWidth: '80px',
                            backgroundColor: '#e9ecef',
                            color: '#495057',
                            cursor: 'default',
                            lineHeight: '1.5',
                          }}
                        >
                          {bmi}
                        </div>
                      </Col>
                    </Form.Group>
                  );
                })()}
              </Form>
              <Card>
                <Card.Header>
                  Complete Blood Count (CBC) w/ Differentials &amp; Platelets
                </Card.Header>
                <Card.Body>
                  <Form noValidate>
                    {PanelField.CBC_FIELDS.map((field: any, index: number) => {
                      return field.isNotEditable ? null : this.formGroup(field);
                    })}
                  </Form>
                </Card.Body>
              </Card>
              <Card>
                <Card.Header>Comprehensive Metabolic Panel (CMP)</Card.Header>
                <Card.Body>
                  <Form noValidate>
                    {PanelField.CMP_FIELDS.map((field: any, index: number) => {
                      return field.isNotEditable ? null : this.formGroup(field);
                    })}
                  </Form>
                </Card.Body>
              </Card>
              <Card>
                <Card.Header>Lipid Panel</Card.Header>
                <Card.Body>
                  <Form noValidate>
                    {PanelField.LIPID_FIELDS.filter(
                      // The nine NMR LipoProfile rows are held back from prod
                      // (nmrLipoProfileVisibility.ts). Filtered here rather
                      // than left out of LIPID_FIELDS, because the list is also
                      // ALL_FIELDS: a field missing from there cannot be
                      // resolved by findField, and a value already stored on
                      // the test would lose its units, precision and label.
                      (field: any) =>
                        (nmrLipoProfileVisible() ||
                          !NMR_PARTICLE_ATTRIBUTES.includes(field.id)) &&
                        // Same reason, same place (vldlVisibility.ts).
                        (vldlVisible() || field.id !== 'vLDL'),
                    ).map((field: any, index: number) => {
                      return field.isNotEditable ? null : this.formGroup(field);
                    })}
                    {/*
                      The three lipid ratios are `isNotEditable` and so never
                      render above, which leaves a gap a practitioner working
                      from a Labcorp report WILL notice: Labcorp prints
                      "T. Chol/HDL Ratio" on its standard lipid panel, so the
                      number is on the page in front of them and there is no box
                      for it. Said once here rather than left to be discovered.
                    */}
                    <div style={LIPID_RATIO_NOTE_STYLE}>
                      Chol/HDL Ratio, TG/HDL Ratio, and Apo B/Apo A-1 are not
                      entered here. LabSmarts calculates all three from the
                      markers above, and they appear on the report and the
                      graphs as usual.
                    </div>
                  </Form>
                </Card.Body>
              </Card>
              <Card>
                <Card.Header>Iron Panel</Card.Header>
                <Card.Body>
                  <Form noValidate>
                    {PanelField.IRON_FIELDS.map((field: any, index: number) => {
                      return field.isNotEditable ? null : this.formGroup(field);
                    })}
                  </Form>
                </Card.Body>
              </Card>
              <Card>
                <Card.Header>Thyroid Panel</Card.Header>
                <Card.Body>
                  <Form noValidate>
                    {PanelField.THYROID_FIELDS.map(
                      (field: any, index: number) => {
                        return field.isNotEditable
                          ? null
                          : this.formGroup(field);
                      },
                    )}
                  </Form>
                </Card.Body>
              </Card>
              <Card>
                <Card.Header>Additional Markers</Card.Header>
                <Card.Body>
                  <Form noValidate>
                    {PanelField.ADDITIONAL_FIELDS.map(
                      (field: any, index: number) => {
                        return field.isNotEditable
                          ? null
                          : this.formGroup(field);
                      },
                    )}
                  </Form>
                </Card.Body>
              </Card>
              <Card>
                <Card.Header>Hormones Panel</Card.Header>
                <Card.Body>
                  <Form noValidate>
                    {PanelField.SEX_HORMONE_FIELDS.map(
                      (field: any, index: number) => {
                        return field.isNotEditable
                          ? null
                          : this.formGroup(field);
                      },
                    )}
                  </Form>
                </Card.Body>
              </Card>
              {/* MVX Plus sits with the shipped panels, directly under
                  Hormones, rather than after the practitioner's own sections
                  (Maryann, 2026-10-09). It is a LabSmarts section now, not an
                  add-on, and the analysis PDF has always printed it here. */}
              {mvxVisible() && this.renderMvxCard()}
              {/* Practitioner-typed markers LabSmarts does not ship yet. Saved
                  with THIS test only — they get their own "Markers Added by the
                  User" section in the analysis and nothing else. */}
              {/* A card per section the practitioner named, then the
                  catch-all. A panel they run whole gets the weight the shipped
                  panels get: its own card with its own name, sitting among
                  them. Only THEIR sections, never a LabSmarts one — a second
                  card headed "Glucose Regulation & Metabolic Function" beside
                  the real glucose panel is two cards with one name. A marker
                  filed under a shipped section stays in the catch-all card
                  under its heading. */}
              {/* Only worth offering once there is more than one card: with no
                  sections of their own there is a single card on the screen, and
                  "collapse all" over one panel is a control that says nothing.
                  Right-aligned above the run, where it reads as applying to all
                  of them rather than to the first. */}
              {this.practitionerMarkerSections().length > 0 ? (
                <div className="custom-marker-cards-toolbar">
                  <Button
                    variant="link"
                    size="sm"
                    className="custom-marker-cards-toggle-all"
                    onClick={() =>
                      this.setAllCardsCollapsed(!this.allCardsCollapsed())
                    }
                  >
                    <FontAwesomeIcon
                      icon={
                        this.allCardsCollapsed()
                          ? 'chevron-down'
                          : 'chevron-right'
                      }
                      style={{ fontSize: '11px' }}
                    />
                    &nbsp;&nbsp;
                    {this.allCardsCollapsed()
                      ? 'Expand all sections'
                      : 'Collapse all sections'}
                  </Button>
                </div>
              ) : null}
              {this.practitionerMarkerSections().map((group, index, cards) => (
                <Card
                  key={group.key}
                  id={markerSectionCardId(group.key)}
                  className={this.sectionCardDropClass(index)}
                  /* The whole card is the drop target, not just its header: a
                     card is tall, and aiming at a 40px band at the top of one
                     is a worse interaction than dropping anywhere on the panel
                     being moved past. Both handlers no-op unless a CARD drag is
                     in progress, which is what keeps them clear of the marker
                     rows' own drags bubbling up from inside. */
                  onDragOver={e => this.onSectionDragOver(e, index)}
                  onDrop={e => this.onSectionDrop(e, index)}
                >
                  <Card.Header>
                    {/* A flex row rather than a float: "Your section" was
                        floated right, which takes it out of the line box and
                        parks it against the top of the header instead of on
                        the same baseline as the name. */}
                    <div className="custom-marker-card-title">
                      {/* One card draws no handle, for the reason My Sections
                          and the marker rows give: there is nowhere to move it
                          to. */}
                      {cards.length > 1 ? (
                        <span
                          className="custom-marker-drag-handle custom-marker-card-drag"
                          role="button"
                          tabIndex={0}
                          draggable
                          aria-label={`Reorder ${group.label}`}
                          title="Drag to reorder, or use the up and down arrow keys. This is the order these sections print in on THIS client's report."
                          onDragStart={e => this.onSectionDragStart(e, index)}
                          onDragEnd={this.onSectionDragEnd}
                          onKeyDown={e => {
                            if (e.key === 'ArrowUp') {
                              e.preventDefault();
                              this.moveSectionCardBy(index, -1);
                            } else if (e.key === 'ArrowDown') {
                              e.preventDefault();
                              this.moveSectionCardBy(index, 1);
                            }
                          }}
                        />
                      ) : null}
                      <CardCollapseToggle
                        collapsed={!!this.state.collapsedCards[group.key]}
                        label={group.label}
                        onToggle={() => this.toggleCard(group.key)}
                      />
                      <span className="custom-marker-card-count">
                        {group.count} marker
                        {group.count === 1 ? '' : 's'}
                      </span>
                      <span className="custom-marker-card-note">
                        Your section
                      </span>
                    </div>
                  </Card.Header>
                  {/* Unmounted rather than hidden, the way the section headings
                      inside the table fold. A folded card keeps its handle, its
                      name and its marker count in the heading, so it is still
                      draggable and still says what is in it. */}
                  {this.state.collapsedCards[group.key] ? null : (
                  <Card.Body>
                    <CustomMarkersTable
                      onlyGroupKey={group.key}
                      expandedSections={
                        this.props.currentUserProfile?.expandedTestSections
                      }
                      rows={this.state.customMarkerRows}
                      sections={
                        this.props.currentUserProfile?.customMarkerSections
                      }
                      clientGender={this.state.selectedClient?.gender}
                      carriedForwardFrom={
                        this.state.customMarkerCarriedForwardFrom
                      }
                      onCreateSection={this.createSection}
                      onRenameSection={this.renameSection}
                      customMarkersAllowed={this.props.currentUserProfile?.canUseCustomMarkers()}
                      onUpgradeNeeded={() =>
                        this.setState({ showCustomMarkersUpgradeModal: true })
                      }
                      onChange={(customMarkerRows: CustomMarkerRow[]) =>
                        this.setState({ customMarkerRows, dirty: true })
                      }
                    />
                  </Card.Body>
                  )}
                </Card>
              ))}
              <Card>
                <Card.Header>
                  <CustomMarkersHeader
                    collapsed={!!this.state.collapsedCards[APPENDIX_CARD_KEY]}
                    onToggle={() => this.toggleCard(APPENDIX_CARD_KEY)}
                  />
                </Card.Header>
                {this.state.collapsedCards[APPENDIX_CARD_KEY] ? null : (
                <Card.Body>
                  <CustomMarkersTable
                    exceptGroupKeys={this.practitionerMarkerSections().map(
                      group => group.key,
                    )}
                    expandedSections={
                      this.props.currentUserProfile?.expandedTestSections
                    }
                    rows={this.state.customMarkerRows}
                    library={
                      this.props.currentUserProfile?.customMarkerLibrary
                    }
                    clientGender={this.state.selectedClient?.gender}
                    sections={
                      this.props.currentUserProfile?.customMarkerSections
                    }
                    onCreateSection={this.createSection}
                    onRenameSection={this.renameSection}
                    customMarkersAllowed={this.props.currentUserProfile?.canUseCustomMarkers()}
                    onUpgradeNeeded={() =>
                      this.setState({ showCustomMarkersUpgradeModal: true })
                    }
                    /* Handed over unconditionally: whether the banner is
                       warranted is decided by the table, off the rows THAT
                       CARD shows. Gated here it was keyed off every row on the
                       test, so the appendix card announced carried-forward
                       rows that had all moved up into the section cards. */
                    carriedForwardFrom={
                      this.state.customMarkerCarriedForwardFrom
                    }
                    onChange={(customMarkerRows: CustomMarkerRow[]) =>
                      this.setState({ customMarkerRows, dirty: true })
                    }
                  />
                </Card.Body>
                )}
              </Card>
              {SHOW_IMMUNE_INFLAMMATORY_CARD &&
                process.env.REACT_APP_ENV !== 'prod' &&
                process.env.REACT_APP_ENV !== 'slava' &&
                this.renderImmuneInflammatoryCard()}
              <Button
                id="submitButton"
                type="submit"
                onClick={this.handleSubmit}
                variant="primary"
                disabled={this.blockedByTestLimit()}
                title={
                  this.blockedByTestLimit()
                    ? 'You have reached the maximum number of test results allowed. Upgrade to save a new test.'
                    : undefined
                }
              >
                Save
              </Button>
              &nbsp;&nbsp;
              <Button
                id="clearButton"
                type="button"
                variant="outline-primary"
                onClick={() => this.clearForm(true)}
              >
                Clear
              </Button>
            </Card.Body>
          )}
        </Card>

        {hideEnterTestResults ? null : (
          <button
            onClick={this.handleSubmit}
            disabled={this.blockedByTestLimit()}
            title={
              this.blockedByTestLimit()
                ? 'You have reached the maximum number of test results allowed. Upgrade to save a new test.'
                : 'Save Test Results'
            }
            style={{
              position: 'fixed',
              bottom: '32px',
              right: '32px',
              height: '44px',
              padding: '0 20px',
              borderRadius: '22px',
              // Reads as unavailable rather than simply not working. The pill
              // is the Save most practitioners reach for, being the one that
              // does not need a scroll, so it has to carry the same answer the
              // banner at the top is giving.
              backgroundColor: this.blockedByTestLimit() ? '#adb5bd' : '#766d66',
              color: '#fff',
              border: 'none',
              cursor: this.blockedByTestLimit() ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: 600,
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              zIndex: 1050,
            }}
          >
            Save
          </button>
        )}
      </div>
    );
  }
}
